import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { IdentityFormData, FormMode, FormValidation, IdentityNavigationState, ValidationError, DocumentProcessingResult, CountryCatalogItem, KycEmployedActivityRow, KycSelfEmployedActivityRow } from "@/screen/Identities/Interfaces/IdentityFormInterfaces";
import { getInitialFormData, getInitialValidation } from "@/screen/Identities/IdentityForm/identityFormInitialState";
import { mapUserIdentityDetailDtoToFormData, buildSaveUserIdentityDraftFormData } from "@/screen/Identities/IdentityForm/userIdentityDetailMapper";
import { resolveDocumentAttachments } from "@/screen/Identities/IdentityForm/identityDocumentAttachments";
import { mapOrchestratorErrorsToValidationErrors, orchestratorErrorsToSingleMessage } from "@/screen/Identities/IdentityForm/identityServerErrors";
import { resolveErrorMessage } from "@/screen/Identities/IdentityForm/identityErrorMessages";
import { isEditFormValid } from "@/screen/Identities/IdentityForm/identityEditValidation";
import { isAddressValid } from "@/screen/Identities/IdentityForm/identityAddressValidation";
import { resolveIdentityDocumentAccess, isExpiredDocumentRenewalIncomplete } from "@/screen/Identities/IdentityForm/identityDocumentAccess";
import { isMinorByBirthday } from "@/screen/Identities/IdentityForm/identityMinorRules";
import { buildRequestAccessPayload } from "@/screen/Identities/IdentityForm/requestAccessPayloadRules";
import { buildIdentityStatusFromSaveResult, type SaveConflictResult } from "@/screen/Identities/IdentityForm/saveConflictFlowRules";
import { resolveEffectiveFlowAction } from "@/screen/Identities/IdentityForm/identityFlowNormalizer";
import { canAcceptTransferAsMain, findEmptyMainBirthCandidateId, isTransferPendingAcceptance, resolveAcceptTransferTargetId, shouldRemoveAcceptTransferParam } from "@/screen/Identities/IdentityForm/acceptTransferRules";
import { isValidPersonName } from "@/utils/personNameRules";
import { getKycValidation, hasSaveBlockingKycAnswers, KycBooleanFields, KycStringCodeFields, MaritalStatusMarriedValue } from "@/screen/Identities/IdentityForm/identityKycRules";
import type { KycActivityKind } from "@/screen/Identities/IdentityKycInputs/Interfaces/IdentityKycInputsInterfaces";
import userIdentitiesService from "@/services/UserIdentitiesService";
import FinametrixService from "@/services/FinametrixService";
import type { UserIdentityCatalogs, UserIdentityDetailDto, ProfessionCatalogItemDto, KycCatalogItemDto, UserIdentityStatusDto, SaveUserIdentityDraftResponseDto } from "@/services/UserIdentitiesService";
import { UserIdentityFlowAction } from "@/services/UserIdentitiesService";
import { parseIsoDate } from "@/utils/DateFormatter";
import { buildIdentitiesListNavigationState, IdentitiesPath, IdentityEditPath, removeAcceptTransferParam } from "@/screen/Identities/identitiesRoutes";
import Auth from "@/Auth/Auth";
import { buildMainPromotionSuccessMessage, isEmptyMainBirthDraft, normalizeIdentityListItems, normalizeMainUserIdentityDto, resolveCreateRedirectToMainDraftId, resolveSaveUserIdentityDraftId } from "@/screen/Identities/holderIdentityDraft";

type RawCatalogItem =
{
    Value?: number | string | null;
    Label?: string | null;
    value?: number | string | null;
    label?: string | null;
};

export type FormState = "document_upload" | "form_filling" | "submitting" | "submitted";
export type IdentityFormSavingAction = "draft" | "finalize";
/** Origen de la confirmación de cambio de principal: cesión automática o aceptación de una cesión aprobada (TRANSFER). */
export type PromoteToMainIntent = "automaticTransfer" | "acceptTransfer";

export interface UseIdentityFormControllerReturn
{
    mode: FormMode;
    userIdentityId: number | undefined;
    formData: IdentityFormData;
    validation: FormValidation;
    formState: FormState;
    isLoading: boolean;
    isSaving: boolean;
    savingAction: IdentityFormSavingAction | null;
    showSaveSuccessModal: boolean;
    closeSaveSuccessModal: () => void;
    isFormLocked: boolean;
    isIdTypeLocked: boolean;
    isEmailLocked: boolean;
    isEditActiveIdentity: boolean;
    canRenewIdentityDocument: boolean;
    isMissingIdentityDocuments: boolean;
    hasStoredIdentityDocuments: boolean;
    isStoredDocumentExpired: boolean;
    isStoredIdExpirationMissing: boolean;
    /** El justificante de tutela es exigible: la fecha de nacimiento dice que es menor. */
    requiresMinorGuardianDocument: boolean;
    hasBirthdayBusinessError: boolean;
    hasIdExpirationBusinessError: boolean;
    highlightPendingManualFields: boolean;
    validationErrors: ValidationError[];
    serverWarning: string | null;
    isFiscalBlocked: boolean;
    identityCatalogs: UserIdentityCatalogs | null;
    countriesCatalog: CountryCatalogItem[];
    catalogsLoading: boolean;
    catalogsError: string | null;
    /** Error de carga de los catálogos CNO/CNAE anclado a los dropdowns KYC. */
    professionCatalogsError: string | null;
    identityStatus: UserIdentityStatusDto | null;
    flowRequestLoading: boolean;
    flowRequestError: string | null;
    flowRequestResultMessage: string | null;
    isFormVisible: boolean;
    canShowFinalizeAction: boolean;
    canFinalizeSignUp: boolean;
    isFormValid: () => boolean;
    handleCancel: () => void;
    handleSubmit: () => Promise<void>;
    handleFinalizeSignUp: () => void;
    handleSubmitFlowRequestAction: () => Promise<void>;
    /** ReactivateOwn: recupera el interviniente propio dado de baja y abre su edición. */
    handleReactivateOwnIdentity: () => Promise<void>;
    /** Panel de flujo visible: recibe el foco cuando aparece tras un guardado rechazado. */
    flowPanelRef: React.RefObject<HTMLDivElement>;
    /**
     * Cesión automática sobre una cuenta que solo tiene el principal vacío de nacimiento: el panel pregunta si el
     * interviniente pasa a ser el principal o se añade como secundario.
     */
    canOfferTransferAsMain: boolean;
    /** Abre la confirmación de «Hacerlo el principal de mi cuenta» en la cesión automática. */
    handleRequestTransferAsMain: () => void;
    /**
     * Interviniente en TRANSFER (recibido por cesión aprobada y aún sin aceptar): en lugar del formulario de datos
     * se pinta el formulario de aceptación. No depende de ningún parámetro de la URL.
     */
    showAcceptTransferPanel: boolean;
    /** La cuenta solo tiene el principal vacío de nacimiento: se ofrece aceptar como principal o como secundario. */
    canAcceptTransferAsMain: boolean;
    /** Comprobando si se puede ofrecer aceptar como principal: los botones esperan para no cambiar bajo el cursor. */
    acceptTransferOptionsLoading: boolean;
    acceptTransferSubmitting: boolean;
    acceptTransferError: string | null;
    /** Aviso tras aceptar como secundario, ya con el formulario editable. */
    acceptTransferSuccessMessage: string | null;
    /** Nombre del interviniente recibido, para el texto del formulario de aceptación. */
    acceptTransferIdentityFullName: string;
    /** «Aceptar como interviniente principal»: abre la confirmación. */
    handleRequestAcceptTransferAsMain: () => void;
    /** «Aceptar como interviniente secundario» o «Aceptar la cesión» (promoteToMain: false). */
    handleAcceptTransfer: () => Promise<void>;
    /** Nombre que muestra la confirmación de cambio de principal. */
    promoteToMainFullName: string;
    showPromoteToMainModal: boolean;
    promoteToMainError: string | null;
    promoteToMainSubmitting: boolean;
    handleConfirmPromoteToMain: () => Promise<void>;
    handleCancelPromoteToMain: () => void;
    handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
    handleKycActivityAdd: (kind: KycActivityKind) => void;
    handleKycActivityRemove: (kind: KycActivityKind, index: number) => void;
    handleKycActivityChange: (kind: KycActivityKind, index: number, field: string, value: string | number | null) => void;
    setIsKycBlocked: (blocked: boolean) => void;
    handleBirthdayChange: (date: Date | null) => void;
    handleIdExpirationChange: (date: Date | null) => void;
    handlePermanentIdExpirationToggle: (ev: React.MouseEvent<HTMLElement>, checked?: boolean) => void;
    handleProofOfMinorGuardianChange: (file: File | null) => void;
    handleProofOfIncomeChange: (file: File | null) => void;
    handleUseSinglePhoneToggle: (ev: React.MouseEvent<HTMLElement>, checked?: boolean) => void;
    handlePostalAddressChange: (field: string, value: string) => void;
    handleFiscalAddressChange: (field: string, value: string) => void;
    handleSameAddressToggle: (ev: React.MouseEvent<HTMLElement>, checked?: boolean) => void;
    errorsRef: React.RefObject<HTMLDivElement>;
    handleDocumentProcessed: (result: DocumentProcessingResult, frontPreview: string, backPreview: string) => void;
    handleDocumentError: (error: string) => void;
    handleDocumentAttachmentErrors: (errors: ValidationError[]) => void;
    handleFinanceAttachmentErrors: (errors: ValidationError[]) => void;
    handleAddressCascadeLoadingChange: (loading: boolean) => void;
    handleDocumentPreviewLoadingChange: (loading: boolean) => void;
    handleServerWarning: (message: string) => void;
    handleDniConfirmed: (dni: string) => void;
    handleFlowResolved: (action: UserIdentityFlowAction | null, status: UserIdentityStatusDto | null) => Promise<void>;
    setIsFiscalBlocked: (blocked: boolean) => void;
    getTitle: () => string;
    getSubtitle: () => string;
}

const BIRTHDAY_ERROR_FIELD = "Fecha de nacimiento";
const BIRTHDAY_ERROR_MESSAGE = "No permitimos fechas de nacimiento futuras.";
const BIRTHDAY_MINOR_ERROR_MESSAGE = "No permitimos el alta de menores con fecha de nacimiento superior a 18 años.";
const BIRTHDAY_VALIDATION_DEBOUNCE_MS = 800;
const ID_EXPIRATION_ERROR_FIELD = "Fecha de validez";
const ID_EXPIRATION_CREATE_ERROR_MESSAGE = "No permitimos el alta de intervinientes cuyos documentos de identidad estén caducados.";
const ID_EXPIRATION_EDIT_ERROR_MESSAGE = "No puedes editar la fecha de validez del documento en fecha perteneciente al pasado.";
const ID_EXPIRATION_VALIDATION_DEBOUNCE_MS = 800;
const EXPIRED_DOCUMENT_RENEWAL_ERROR_FIELD = "Documento de identidad";
const EXPIRED_DOCUMENT_RENEWAL_ERROR_MESSAGE = "El documento de identidad guardado está caducado. Para poder guardar los cambios debes adjuntar de nuevo el anverso y el reverso del DNI/NIE junto a una nueva fecha de validez.";
const DOCUMENT_ATTACHMENT_ERROR_FIELDS = ["Documento. Anverso Documento de Identidad", "Documento. Reverso Documento de Identidad"];
const FINANCE_ATTACHMENT_ERROR_FIELDS = ["Documento. Prueba de Ingreso"];
const FATCA_STATUS_ALLOWED_LABEL = "No sujeto a comunicación FATCA";
const FATCA_STATUS_ALLOWED_LABEL_ALT = "No sujeto a comunicación FACTA";

function toStartOfDay(value: Date): Date
{
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
}

function getDateBusinessValidation(birthday: Date | null, idExpiration: Date | null, mode: FormMode, hasPermanentIdExpiration: boolean = false): {hasInvalidBirthday: boolean; birthdayErrorMessage: string; hasInvalidIdExpiration: boolean; idExpirationErrorMessage: string}
{
    const todayStart = toStartOfDay(new Date());
    const selectedBirthdayStart = birthday ? toStartOfDay(birthday) : null;
    const selectedIdExpirationStart = idExpiration ? toStartOfDay(idExpiration) : null;
    const eighteenYearsAgo = new Date(todayStart);
    eighteenYearsAgo.setFullYear(todayStart.getFullYear() - 18);

    const hasFutureBirthday = selectedBirthdayStart !== null && selectedBirthdayStart.getTime() > todayStart.getTime();
    // Esta regla protege el flujo explícito de "Alta de Interviniente Menor de Edad": por ahí no se da de
    // alta a un adulto. NO puede depender de la minoría de edad de los datos, porque la edad de un
    // interviniente ya existente es un hecho y no una elección: al cumplir los 18 su propia fecha (correcta
    // y sin tocar) quedaba marcada como inválida y su formulario nacía muerto, ya que el flag isMinor se
    // queda pegado a true de por vida (ver identityMinorRules).
    const isMinorSignUpFlow = mode === "createMinor";
    const hasAdultBirthdayForMinor = isMinorSignUpFlow && selectedBirthdayStart !== null && selectedBirthdayStart.getTime() <= eighteenYearsAgo.getTime();
    const hasInvalidBirthday = hasFutureBirthday || hasAdultBirthdayForMinor;
    const birthdayErrorMessage = hasAdultBirthdayForMinor ? BIRTHDAY_MINOR_ERROR_MESSAGE : BIRTHDAY_ERROR_MESSAGE;

    // La validez permanente no caduca nunca, así que las reglas de caducidad no le aplican.
    const hasInvalidIdExpirationForCreate = mode !== "edit" && selectedIdExpirationStart !== null && selectedIdExpirationStart.getTime() <= todayStart.getTime();
    const hasInvalidIdExpirationForEdit = mode === "edit" && selectedIdExpirationStart !== null && selectedIdExpirationStart.getTime() < todayStart.getTime();
    const hasInvalidIdExpiration = !hasPermanentIdExpiration && (hasInvalidIdExpirationForCreate || hasInvalidIdExpirationForEdit);
    const idExpirationErrorMessage = mode === "edit" ? ID_EXPIRATION_EDIT_ERROR_MESSAGE : ID_EXPIRATION_CREATE_ERROR_MESSAGE;

    return{
        hasInvalidBirthday,
        birthdayErrorMessage,
        hasInvalidIdExpiration,
        idExpirationErrorMessage,
    };
}

function upsertValidationError(prev: ValidationError[], field: string, message: string): ValidationError[]
{
    const withoutField = prev.filter((error) => error.field !== field);
    return [...withoutField, {field, message}];
}

function normalizeCatalogItems(items: unknown): {Value: number; Label: string}[]
{
    if(!Array.isArray(items))
    {
        return [];
    }

    return items.reduce<{Value: number; Label: string}[]>((acc, item) =>
    {
        if(!item || typeof item !== "object")
        {
            return acc;
        }

        const raw = item as RawCatalogItem;
        const rawValue = raw.Value ?? raw.value;
        const rawLabel = raw.Label ?? raw.label;
        const parsedValue = typeof rawValue === "number" ? rawValue : Number(rawValue);
        const parsedLabel = typeof rawLabel === "string" ? rawLabel.trim() : "";

        if(Number.isNaN(parsedValue) || parsedLabel === "")
        {
            return acc;
        }

        acc.push(
        {
            Value: parsedValue,
            Label: parsedLabel,
        });
        return acc;
    }, []);
}

type RawKycCatalogItem =
{
    Code?: string | null;
    Label?: string | null;
    code?: string | null;
    label?: string | null;
};

/** Normaliza los catálogos KYC (código string + literal), tolerando Pascal/camelCase. */
function normalizeKycCatalogItems(items: unknown): KycCatalogItemDto[]
{
    if(!Array.isArray(items))
    {
        return [];
    }

    return items.reduce<KycCatalogItemDto[]>((acc, item) =>
    {
        if(!item || typeof item !== "object")
        {
            return acc;
        }
        const raw = item as RawKycCatalogItem;
        const code = typeof (raw.Code ?? raw.code) === "string" ? String(raw.Code ?? raw.code).trim() : "";
        const label = typeof (raw.Label ?? raw.label) === "string" ? String(raw.Label ?? raw.label).trim() : "";
        if(code === "" || label === "")
        {
            return acc;
        }
        acc.push({code, label});
        return acc;
    }, []);
}

/** Normaliza la matriz origen de fondos → documentaciones permitidas, tolerando Pascal/camelCase. */
function normalizeAllowedDocumentationMatrix(raw: unknown): Record<string, string[]> | null
{
    if(!raw || typeof raw !== "object" || Array.isArray(raw))
    {
        return null;
    }
    const matrix: Record<string, string[]> = {};
    for(const [key, value] of Object.entries(raw as Record<string, unknown>))
    {
        if(Array.isArray(value))
        {
            matrix[key] = value.filter((entry): entry is string => typeof entry === "string");
        }
    }
    return Object.keys(matrix).length > 0 ? matrix : null;
}

/** Normaliza los ítems de los catálogos Cno/Cnae de Omnia. */
function normalizeProfessionCatalogItems(items: unknown): ProfessionCatalogItemDto[]
{
    if(!Array.isArray(items))
    {
        return [];
    }
    return items.reduce<ProfessionCatalogItemDto[]>((acc, item) =>
    {
        if(!item || typeof item !== "object")
        {
            return acc;
        }
        const raw = item as {code?: string; description?: string; nextportfolioId?: string | null};
        if(typeof raw.code !== "string" || typeof raw.description !== "string")
        {
            return acc;
        }
        acc.push({code: raw.code, description: raw.description, nextportfolioId: raw.nextportfolioId ?? null});
        return acc;
    }, []);
}

function normalizeLabel(label: string | null | undefined): string
{
    if(!label)
    {
        return "";
    }
    return label
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/\s+/g, " ")
        .trim()
        .toUpperCase();
}

function getCatalogLabelByValue(items: {Value: number; Label: string}[] | null | undefined, value: number | null | undefined): string
{
    if(value === null || value === undefined || !items || items.length === 0)
    {
        return "";
    }
    const item = items.find((opt) => opt.Value === value);
    return normalizeLabel(item?.Label);
}

function isAllowedFatcaStatus(label: string): boolean
{
    const normalizedAllowed = normalizeLabel(FATCA_STATUS_ALLOWED_LABEL);
    const normalizedAllowedAlt = normalizeLabel(FATCA_STATUS_ALLOWED_LABEL_ALT);
    return label === normalizedAllowed || label === normalizedAllowedAlt;
}

const VALIDATION_FIELDS = getInitialValidation();
const PersonNameFields = ["firstName", "lastName", "secondLastName"];

export function useIdentityFormController(): UseIdentityFormControllerReturn
{
    const history = useHistory();
    const location = useLocation<IdentityNavigationState>();
    const params = useParams<{userIdentityId?: string}>();

    const getModeFromPath = (): FormMode =>
    {
        const path = location.pathname;
        if(path.endsWith("/new"))
        {
            return "create";
        }
        if(path.endsWith("/minor"))
        {
            return "createMinor";
        }
        // Incluye /identities/manage/edit y /identities/manage/edit/:userIdentityId
        if(path.includes("/manage/edit"))
        {
            return "edit";
        }
        return "create";
    };

    const mode = getModeFromPath();

    const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const queryContractFlow = queryParams.get("contractFlow") === "true";
    const querySavingsPlanIdRaw = queryParams.get("savingsPlanId");
    const querySavingsPlanId = querySavingsPlanIdRaw != null && querySavingsPlanIdRaw !== ""
        ? Number(querySavingsPlanIdRaw)
        : undefined;
    const querySavingsPlanName = queryParams.get("savingsPlanName") || "";
    const isDeepLinkContractMode = queryContractFlow
        && querySavingsPlanId != null
        && !Number.isNaN(querySavingsPlanId);

    const routeUserIdentityId = params.userIdentityId != null && params.userIdentityId !== ""
        ? Number(params.userIdentityId)
        : undefined;
    const userIdentityId = location.state?.userIdentityId
        ?? (routeUserIdentityId != null && !Number.isNaN(routeUserIdentityId) ? routeUserIdentityId : undefined);
    const preloadedIdentityDetail = location.state?.preloadedIdentityDetail;

    // El contexto de contrato llega por state desde el listado. El deep-link MTA (querystring) se
    // mantiene aparte y vuelve al mapa.
    const contractSavingsPlanId = location.state?.contractSavingsPlanId;

    const navigateAfterForm = (savedSuccessMessage?: string) =>
    {
        // Deep-link MTA (querystring): vuelve al mapa (ahí se pulsa el paso habilitado).
        if(isDeepLinkContractMode && querySavingsPlanId != null)
        {
            history.push("/configurationMap",
            {
                savingPlanId: querySavingsPlanId,
                savingPlanName: querySavingsPlanName,
            });
            return;
        }

        history.push(IdentitiesPath, buildIdentitiesListNavigationState(contractSavingsPlanId, savedSuccessMessage));
    };

    // Redirigir si se intenta editar sin id
    useEffect(() =>
    {
        if(mode === "edit" && !userIdentityId)
        {
            if(isDeepLinkContractMode && querySavingsPlanId != null)
            {
                history.replace("/configurationMap",
                {
                    savingPlanId: querySavingsPlanId,
                    savingPlanName: querySavingsPlanName,
                });
                return;
            }
            history.replace(IdentitiesPath, buildIdentitiesListNavigationState(contractSavingsPlanId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, userIdentityId, history]);

    const [isLoading, setIsLoading] = useState(mode === "edit" ? !!userIdentityId : true);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [savingAction, setSavingAction] = useState<IdentityFormSavingAction | null>(null);
    const [showSaveSuccessModal, setShowSaveSuccessModal] = useState(false);
    const [formData, setFormData] = useState<IdentityFormData>(getInitialFormData(mode));
    // Acceso síncrono al estado actual del formulario desde callbacks que no lo tienen como dependencia.
    const formDataRef = useRef<IdentityFormData>(formData);
    formDataRef.current = formData;
    const [validation, setValidation] = useState<FormValidation>(getInitialValidation());
    const [formState, setFormState] = useState<FormState>(mode === "edit" ? "form_filling" : "document_upload");
    const [documentPhaseComplete, setDocumentPhaseComplete] = useState(mode === "edit");
    const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
    const [serverWarning, setServerWarning] = useState<string | null>(null);
    const [isFiscalBlocked, setIsFiscalBlocked] = useState<boolean>(false);
    // Bloqueo de alta por respuestas KYC: NO deshabilita el guardado (D1: la respuesta se
    // persiste siempre y el servidor devuelve el bloqueo), solo la finalización. Las respuestas
    // marcadas con preventsSave sí bloquean el guardado y se evalúan aparte en isFormValid.
    const [isKycBlocked, setIsKycBlocked] = useState<boolean>(false);
    const [identityCatalogs, setIdentityCatalogs] = useState<UserIdentityCatalogs | null>(null);
    const identityCatalogsRef = useRef(identityCatalogs);
    identityCatalogsRef.current = identityCatalogs;
    const [countriesCatalog, setCountriesCatalog] = useState<CountryCatalogItem[]>([]);
    const [catalogsLoading, setCatalogsLoading] = useState<boolean>(true);
    const [catalogsError, setCatalogsError] = useState<string | null>(null);
    const [professionCatalogsError, setProfessionCatalogsError] = useState<string | null>(null);
    const [identityStatus, setIdentityStatus] = useState<UserIdentityStatusDto | null>(null);
    const [flowRequestLoading, setFlowRequestLoading] = useState<boolean>(false);
    const [flowRequestError, setFlowRequestError] = useState<string | null>(null);
    const [flowRequestResultMessage, setFlowRequestResultMessage] = useState<string | null>(null);
    // True cuando identityStatus no viene del análisis del documento sino de un guardado rechazado por DNI ya
    // existente: ese estado se descarta en cuanto el usuario cambia el DNI (p. ej. si lo había tecleado mal).
    const saveDerivedIdentityStatusRef = useRef<boolean>(false);
    const flowPanelRef = useRef<HTMLDivElement>(null);
    const [promoteToMainIntent, setPromoteToMainIntent] = useState<PromoteToMainIntent | null>(null);
    const [promoteToMainError, setPromoteToMainError] = useState<string | null>(null);
    const [promoteToMainSubmitting, setPromoteToMainSubmitting] = useState<boolean>(false);
    const [canAcceptTransferAsMainOption, setCanAcceptTransferAsMainOption] = useState<boolean>(false);
    const [acceptTransferOptionsLoading, setAcceptTransferOptionsLoading] = useState<boolean>(false);
    const [acceptTransferSubmitting, setAcceptTransferSubmitting] = useState<boolean>(false);
    const [acceptTransferError, setAcceptTransferError] = useState<string | null>(null);
    const [acceptTransferSuccessMessage, setAcceptTransferSuccessMessage] = useState<string | null>(null);
    const [loadedIdentityDetail, setLoadedIdentityDetail] = useState<UserIdentityDetailDto | null>(preloadedIdentityDetail ?? null);
    const [isAddressCascadeLoading, setIsAddressCascadeLoading] = useState<boolean>(false);
    const [isDocumentPreviewLoading, setIsDocumentPreviewLoading] = useState<boolean>(false);
    // Id del Main NEW creado al pagar: el PATCH debe ir a esa fila, nunca a saveUserIdentity/0.
    const [mainNewDraftId, setMainNewDraftId] = useState<number | null>(null);
    const mainNewDraftIdRef = useRef<number | null>(null);
    mainNewDraftIdRef.current = mainNewDraftId;
    const errorsRef = useRef<HTMLDivElement>(null);
    const birthdayValidationTimeoutRef = useRef<number | null>(null);
    const idExpirationValidationTimeoutRef = useRef<number | null>(null);

    // El titular mínimo nace al pagar (Main + NEW). Un alta con Id=0 crearía una segunda fila.
    useEffect(() =>
    {
        if(mode === "edit")
        {
            return;
        }

        const userId = Auth.getUserProfile()?.id;
        if(!userId)
        {
            setIsLoading(false);
            return;
        }

        let cancelled = false;
        userIdentitiesService.getMainIdentityByUserId(userId).then((env) =>
        {
            if(cancelled)
            {
                return;
            }
            const main = env.success ? normalizeMainUserIdentityDto(env.data) : null;
            const redirectId = resolveCreateRedirectToMainDraftId(mode, main);
            if(redirectId != null)
            {
                setMainNewDraftId(redirectId);
                history.replace(IdentityEditPath,
                {
                    userIdentityId: redirectId,
                    contractSavingsPlanId: contractSavingsPlanId,
                });
                return;
            }
            setMainNewDraftId(null);
            setIsLoading(false);
        })
        .catch(() =>
        {
            if(!cancelled)
            {
                setMainNewDraftId(null);
                setIsLoading(false);
            }
        });

        return () =>
        {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, history]);
    // El bloqueo del formulario sólo depende de completar la fase de documento/orquestador.
    // Los errores de validación (incl. reglas de negocio de fechas) se muestran pero no deshabilitan la edición.
    const isFormLocked = !documentPhaseComplete && mode !== "edit";
    // El número y el tipo de documento son inmutables si ya hay un DNI fijado (tipo DNI y número informado)
    // y el interviniente está sincronizado. Réplica exacta de ValidateIdDocumentImmutableExceptNieToDni del
    // backend: deja abiertos el borrador aún en NEW (corrección de una lectura errónea del OCR), NIE → DNI
    // (nacionalización) y el caso sin documento previo (relleno inicial o DNI que no se pudo extraer).
    const isIdTypeLocked = loadedIdentityDetail !== null &&
        String(loadedIdentityDetail.status ?? "").trim().toUpperCase() !== "NEW" &&
        String(loadedIdentityDetail.idType ?? "").trim().toUpperCase() === "DNI" &&
        String(loadedIdentityDetail.dni ?? "").trim() !== "";
    // El email del principal es su credencial de acceso, no un dato del documento: una vez formalizado no se
    // toca. Réplica de ValidateMainCannotEditEmail del backend, que sí deja corregir nombre y apellidos y
    // admite el primer relleno de un email vacío (datos legacy), de ahí la última condición.
    const isEmailLocked = loadedIdentityDetail !== null &&
        loadedIdentityDetail.main === true &&
        String(loadedIdentityDetail.status ?? "").trim().toUpperCase() !== "NEW" &&
        String(loadedIdentityDetail.email ?? "").trim() !== "";
    const loadedIdentityStatus = String(loadedIdentityDetail?.status ?? "").trim().toUpperCase();
    const isEditActiveIdentity = mode === "edit" && loadedIdentityStatus === "ACTIVE";
    // Interviniente recibido por cesión aprobada y aún sin aceptar: el backend rechaza editarlo, sincronizarlo,
    // borrarlo, firmar su KYC… (TRANSFER_PENDING_ACCEPTANCE), así que lo único que se ofrece es aceptar la cesión.
    const showAcceptTransferPanel = mode === "edit" && !isLoading && isTransferPendingAcceptance(loadedIdentityStatus);
    const canShowFinalizeAction = mode !== "edit" || !isEditActiveIdentity;
    const {isMissingIdentityDocuments, canRenewIdentityDocument, hasStoredIdentityDocuments, isStoredDocumentExpired, isStoredIdExpirationMissing} =
        resolveIdentityDocumentAccess(mode, loadedIdentityDetail, formData);
    // Justificante de tutela ya persistido: sigue siendo obligatorio para los menores, pero uno que ya
    // está en BD lo cumple sin volver a subirlo (es lo que valida el backend en
    // ValidateProofOfMinorGuardianWhenMinor, sobre ProofOfMinorGuardianDescription).
    const hasStoredProofOfMinorGuardian = loadedIdentityDetail?.hasProofOfMinorGuardian === true;
    // La obligatoriedad del justificante de tutela la marca la fecha de nacimiento, no el flujo ni el flag
    // persistido: es lo que hace el backend en ValidateProofOfMinorGuardianWhenMinor, que además se aplica
    // tanto al guardar el borrador como al formalizar.
    const requiresMinorGuardianDocument = isMinorByBirthday(formData.birthday);
    // Bloqueo anticipado por documento caducado: el backend lo evalúa sobre la fecha PERSISTIDA, así que
    // sin este espejo el guardado salía con una fecha futura en pantalla y volvía con "documento caducado".
    const isExpiredIdentityDocumentRenewalIncomplete = isExpiredDocumentRenewalIncomplete(loadedIdentityDetail, formData);
    const hasBirthdayBusinessError = validationErrors.some((error) => error.field === BIRTHDAY_ERROR_FIELD);
    // El bloqueo por caducidad también marca en rojo la fecha de validez, que es donde el usuario la mira.
    const hasIdExpirationBusinessError = validationErrors.some((error) => error.field === ID_EXPIRATION_ERROR_FIELD) || isExpiredIdentityDocumentRenewalIncomplete;

    const applyLoadedIdentityData = useCallback((detailDto: UserIdentityDetailDto) =>
    {
        setLoadedIdentityDetail(detailDto);
        const data = mapUserIdentityDetailDtoToFormData(detailDto);
        const dateBusinessValidation = getDateBusinessValidation(data.birthday, data.idExpiration, mode, data.hasPermanentIdExpiration);
        const hasLoadedIdFront = detailDto.hasDniFront || !!data.idFront;
        const hasLoadedIdBack = detailDto.hasDniBack || !!data.idBack;
        const hasLoadedProofOfIncome = detailDto.hasProofOfIncome || !!data.proofOfIncome;
        const hasLoadedProofOfMinorGuardian = detailDto.hasProofOfMinorGuardian || !!data.proofOfMinorGuardian;
        const hasStoredDni = !!String(data.idDocument ?? "").trim();
        const hasLoadedIdentityDocuments = hasLoadedIdFront && hasLoadedIdBack;
        setFormData(data);
        if(mode === "edit" && data.useSinglePhone)
        {
            setServerWarning("Hemos detectado que los teléfonos de contacto, operación y firma coinciden, por lo que se ha activado el uso de un único teléfono. Desmárcalo si quieres indicar teléfonos distintos.");
        }
        // Si no se puede recuperar el documento, tenemos que persistir el DNI para poder continuar con el alta.
        setDocumentPhaseComplete(hasStoredDni || !hasLoadedIdentityDocuments);
        setValidation((prev) =>
        ({
            ...prev,
            firstName: !!data.firstName,
            lastName: !!data.lastName,
            secondLastName: !!data.secondLastName,
            email: !!data.email,
            phone: !!data.phone,
            operationPhone: !!data.operationPhone,
            signaturePhone: !!data.signaturePhone,
            countryPrefix: !!data.countryPrefix,
            operationCountryPrefix: !!data.operationCountryPrefix,
            signatureCountryPrefix: !!data.signatureCountryPrefix,
            birthday: !!data.birthday && !dateBusinessValidation.hasInvalidBirthday,
            birthdayCountry: !!data.birthdayCountry,
            nationalCountry: !!data.nationalCountry,
            idDocument: !!data.idDocument,
            // Con validez permanente el campo de fecha va vacío a propósito: el dato válido es el check.
            idExpiration: (data.hasPermanentIdExpiration || !!data.idExpiration) && !dateBusinessValidation.hasInvalidIdExpiration,
            idFront: hasLoadedIdFront,
            idBack: hasLoadedIdBack,
            postalAddress: isAddressValid(data.postalAddress),
            fiscalAddress: data.sameAddress || isAddressValid(data.fiscalAddress),
            economicActivity: !!data.economicActivity,
            proofOfIncome: hasLoadedProofOfIncome,
            // Obligatorio para los menores. Un justificante ya guardado lo cumple sin volver a subirlo:
            // sin esto un menor con su justificante en BD lo leía como pendiente y no se podía editar.
            proofOfMinorGuardian: hasLoadedProofOfMinorGuardian,
            gafiQuestion: data.gafiQuestion !== undefined && data.gafiQuestion !== null,
            publicPosition: data.publicPosition !== undefined && data.publicPosition !== null,
            maritalStatus: data.maritalStatus !== undefined && data.maritalStatus !== null,
            profession: !!data.profession,
            residenceCountry: !!data.residenceCountry,
            fatcaStatus: data.fatcaStatus !== undefined && data.fatcaStatus !== null,
            residenceOfficialDocument: data.residenceOfficialDocument !== undefined && data.residenceOfficialDocument !== null,
            noResidenceDocument: data.noResidenceDocument !== undefined && data.noResidenceDocument !== null,
            hasTaxIdInUS: data.hasTaxIdInUS !== undefined && data.hasTaxIdInUS !== null,
            hasResidenceInUS: data.hasResidenceInUS !== undefined && data.hasResidenceInUS !== null,
            hasTaxObligationInUS: data.hasTaxObligationInUS !== undefined && data.hasTaxObligationInUS !== null
        }));
        setValidationErrors((prev) =>
        {
            let nextErrors = prev.filter((error) => error.field !== BIRTHDAY_ERROR_FIELD && error.field !== ID_EXPIRATION_ERROR_FIELD);
            if(dateBusinessValidation.hasInvalidBirthday)
            {
                nextErrors = upsertValidationError(nextErrors, BIRTHDAY_ERROR_FIELD, dateBusinessValidation.birthdayErrorMessage);
            }
            if(dateBusinessValidation.hasInvalidIdExpiration)
            {
                nextErrors = upsertValidationError(nextErrors, ID_EXPIRATION_ERROR_FIELD, dateBusinessValidation.idExpirationErrorMessage);
            }
            return nextErrors;
        });
    }, [mode]);

    // Flujos que ocultan el formulario estándar y muestran un UX específico desde el backend
    const FLOWS_HIDING_FORM: UserIdentityFlowAction[] =
    [
        UserIdentityFlowAction.RequestAccess,
        UserIdentityFlowAction.InformationManualSignUpRequired,
        UserIdentityFlowAction.RequestOwnershipTransfer,
        UserIdentityFlowAction.AutomaticOwnershipTransfer,
        UserIdentityFlowAction.ReactivateOwn,
    ];
    const isFormVisible = identityStatus === null || !FLOWS_HIDING_FORM.includes(identityStatus.action);

    useEffect(() =>
    {
        let cancelled = false;
        setCatalogsLoading(true);
        setCatalogsError(null);
        setProfessionCatalogsError(null);

        Promise.all([userIdentitiesService.getUserIdentityCatalogs(), FinametrixService.getCountries(), userIdentitiesService.getProfessionCatalogs()]).then(([catRes, countriesRes, professionCatalogsRes]) =>
        {
            if(cancelled)
            {
                return;
            }

            const rawCountries: unknown[] = Array.isArray(countriesRes) ? countriesRes : [];
            const countryTextCollator = new Intl.Collator("es", {sensitivity: "base"});
            const normalizedCountries = rawCountries.filter((country) => country && typeof country === "object")
                .map((country) =>
                ({
                    key: String((country as {key?: string; value?: string}).key ?? (country as {value?: string}).value ?? ""),
                    value: String((country as {value?: string}).value ?? ""),
                    text: String((country as {text?: string; value?: string}).text ?? (country as {value?: string}).value ?? ""),
                }))
                .filter((country) => country.value.trim() !== "" && country.text.trim() !== "")
                .sort((a, b) => countryTextCollator.compare(a.text, b.text));
            setCountriesCatalog(normalizedCountries);

            // Catálogos Cno/Cnae de Omnia para las actividades económicas KYC
            const professionCatalogsData = professionCatalogsRes.success ? (professionCatalogsRes.data as Record<string, unknown> | null) : null;
            // Error CNO/CNAE: se ancla a los dropdowns de actividad, no al título KYC.
            setProfessionCatalogsError(professionCatalogsRes.success ? null : (orchestratorErrorsToSingleMessage(professionCatalogsRes.errors) || "No se pudieron cargar los catálogos de actividades económicas."));

            if(!catRes.success)
            {
                const msg = orchestratorErrorsToSingleMessage(catRes.errors);
                setCatalogsError(msg || "No se pudieron cargar los catálogos fiscales.");
                setIdentityCatalogs(null);
                return;
            }

            const d = catRes.data as Record<string, unknown> | null;
            setCatalogsError(null);
            setIdentityCatalogs(
            {
                MaritalStatuses: normalizeCatalogItems(d?.MaritalStatuses ?? d?.maritalStatuses),
                FatcaStatuses: normalizeCatalogItems(d?.FatcaStatuses ?? d?.fatcaStatuses),
                ResidenceOfficialDocuments: normalizeCatalogItems(d?.ResidenceOfficialDocuments ?? d?.residenceOfficialDocuments),
                NoResidenceDocuments: normalizeCatalogItems(d?.NoResidenceDocuments ?? d?.noResidenceDocuments),
                AnnualGrossIncomes: normalizeKycCatalogItems(d?.AnnualGrossIncomes ?? d?.annualGrossIncomes),
                FundsOrigins: normalizeKycCatalogItems(d?.FundsOrigins ?? d?.fundsOrigins),
                FundsDocumentationTypes: normalizeKycCatalogItems(d?.FundsDocumentationTypes ?? d?.fundsDocumentationTypes),
                PersonalCircumstances: normalizeKycCatalogItems(d?.PersonalCircumstances ?? d?.personalCircumstances),
                MaritalSituations: normalizeKycCatalogItems(d?.MaritalSituations ?? d?.maritalSituations),
                RelatedProfessionalActivities: normalizeKycCatalogItems(d?.RelatedProfessionalActivities ?? d?.relatedProfessionalActivities),
                EconomicActivityStatuses: normalizeKycCatalogItems(d?.EconomicActivityStatuses ?? d?.economicActivityStatuses),
                AllowedFundsDocumentationByOrigin: normalizeAllowedDocumentationMatrix(d?.AllowedFundsDocumentationByOrigin ?? d?.allowedFundsDocumentationByOrigin),
                Cnos: normalizeProfessionCatalogItems(professionCatalogsData?.cnos),
                Cnaes: normalizeProfessionCatalogItems(professionCatalogsData?.cnaes),
            });
        })
        .catch(() =>
        {
            if(!cancelled)
            {
                setCatalogsError("No se pudieron cargar los catálogos fiscales. Reintenta más tarde.");
                setProfessionCatalogsError(null);
                setIdentityCatalogs(null);
                setCountriesCatalog([]);
            }
        })
        .finally(() =>
        {
            if(!cancelled)
            {
                setCatalogsLoading(false);
            }
        });
        return () =>
        {
            cancelled = true;
        };
    }, []);

    useEffect(() =>
    {
        if(mode !== "edit" || !preloadedIdentityDetail)
        {
            return;
        }

        applyLoadedIdentityData(preloadedIdentityDetail);
        setIsLoading(true);
    }, [mode, preloadedIdentityDetail, applyLoadedIdentityData]);

    useEffect(() =>
    {
        const selectedFatcaStatusLabel = getCatalogLabelByValue(identityCatalogs?.FatcaStatuses, formData.fatcaStatus);
        const fatcaStatusValid = formData.fatcaStatus === null || formData.fatcaStatus === undefined ? false : isAllowedFatcaStatus(selectedFatcaStatusLabel);

        setValidation((prev) => prev.fatcaStatus === fatcaStatusValid ? prev : {...prev, fatcaStatus: fatcaStatusValid});
    }, [identityCatalogs, formData.fatcaStatus]);

    // Validación del bloque KYC: se recalcula íntegra desde el estado del formulario
    // (incluye condicionales: otra nacionalidad, régimen matrimonial y filas de actividades).
    useEffect(() =>
    {
        const kycValidation = getKycValidation(formData, identityCatalogs?.AllowedFundsDocumentationByOrigin);
        setValidation((prev) => ({...prev, ...kycValidation}));
    }, [formData, identityCatalogs?.AllowedFundsDocumentationByOrigin]);

    useEffect(() =>
    {
        const postalValid = isAddressValid(formData.postalAddress);
        const fiscalValid = formData.sameAddress ? postalValid : isAddressValid(formData.fiscalAddress);
        setValidation((prev) =>
        {
            if(prev.postalAddress === postalValid && prev.fiscalAddress === fiscalValid)
            {
                return prev;
            }
            return {...prev, postalAddress: postalValid, fiscalAddress: fiscalValid};
        });
    }, [formData.postalAddress, formData.fiscalAddress, formData.sameAddress]);

    // Aviso del bloqueo por documento caducado. Se mantiene sincronizado con el flag para que desaparezca
    // en cuanto se adjuntan las dos caras, y no se le roba el foco al usuario: no es una reacción a lo que
    // acaba de escribir, sino un estado del interviniente que ya venía de servidor.
    useEffect(() =>
    {
        setValidationErrors((prev) =>
        {
            const withoutRenewalBlock = prev.filter((error) => error.field !== EXPIRED_DOCUMENT_RENEWAL_ERROR_FIELD);
            if(!isExpiredIdentityDocumentRenewalIncomplete)
            {
                return withoutRenewalBlock.length === prev.length ? prev : withoutRenewalBlock;
            }
            return upsertValidationError(withoutRenewalBlock, EXPIRED_DOCUMENT_RENEWAL_ERROR_FIELD, EXPIRED_DOCUMENT_RENEWAL_ERROR_MESSAGE);
        });
    }, [isExpiredIdentityDocumentRenewalIncomplete]);

    // Cambio de interviniente sin desmontar el formulario (p. ej. tras recuperar uno propio dado de baja se
    // navega a su edición, que es la misma ruta): se descarta el estado de flujo del anterior y se vuelve a
    // la carga, que además remonta la subida de documento con las imágenes del nuevo interviniente.
    const previousUserIdentityIdRef = useRef<number | undefined>(userIdentityId);
    useEffect(() =>
    {
        if(previousUserIdentityIdRef.current === userIdentityId)
        {
            return;
        }
        previousUserIdentityIdRef.current = userIdentityId;
        if(mode !== "edit" || !userIdentityId)
        {
            return;
        }
        saveDerivedIdentityStatusRef.current = false;
        setIdentityStatus(null);
        setFlowRequestLoading(false);
        setFlowRequestError(null);
        setFlowRequestResultMessage(null);
        setValidationErrors([]);
        setServerWarning(null);
        setPromoteToMainIntent(null);
        setPromoteToMainError(null);
        setAcceptTransferError(null);
        setAcceptTransferSuccessMessage(null);
        setIsLoading(true);
    }, [mode, userIdentityId]);

    // Carga de datos en modo edición
    useEffect(() =>
    {
        if(mode !== "edit" || !userIdentityId)
        {
            return;
        }

        let cancelled = false;

        userIdentitiesService.getUserIdentityDetails(userIdentityId).then((res) =>
        {
            if(cancelled)
            {
                return;
            }
            if(!res.success)
            {
                const mapped = mapOrchestratorErrorsToValidationErrors(res.errors);
                setValidationErrors(mapped.length > 0 ? mapped : [{ field: "General", message: "No se pudieron cargar los datos del interviniente." }]);
                return;
            }

            if(!res.data)
            {
                setValidationErrors([{ field: "General", message: "No se pudieron cargar los datos del interviniente." }]);
                return;
            }

            applyLoadedIdentityData(res.data as UserIdentityDetailDto);
        })
        .catch(() =>
        {
            if(!cancelled)
            {
                setValidationErrors([{ field: "General", message: "No se pudieron cargar los datos del interviniente. Reintenta más tarde." }]);
            }
        })
        .finally(() =>
        {
            if(!cancelled)
            {
                setIsLoading(false);
            }
        });

        return () =>
        {
            cancelled = true;
        };
    }, [mode, userIdentityId, applyLoadedIdentityData]);

    // Formulario de aceptación: «Aceptar como interviniente principal» solo se ofrece si la cuenta únicamente tiene
    // el principal vacío de nacimiento. Se decide con datos propios (listado byUser y detalle del principal); si
    // alguna llamada falla se ofrece solo «Aceptar la cesión». El backend lo valida igualmente al aceptar.
    useEffect(() =>
    {
        const userId = Auth.getUserProfile()?.id;
        if(!showAcceptTransferPanel || !userIdentityId || !userId)
        {
            setCanAcceptTransferAsMainOption(false);
            setAcceptTransferOptionsLoading(false);
            return;
        }

        let cancelled = false;
        setAcceptTransferOptionsLoading(true);

        const resolveCanAcceptTransferAsMain = async (): Promise<boolean> =>
        {
            const byUserEnv = await userIdentitiesService.getUserIdentitiesByUserId(userId);
            if(!byUserEnv.success)
            {
                return false;
            }
            const accountIdentities = normalizeIdentityListItems(byUserEnv.data);
            const mainCandidateId = findEmptyMainBirthCandidateId(accountIdentities, userIdentityId);
            if(mainCandidateId === null)
            {
                return false;
            }
            const mainDetailEnv = await userIdentitiesService.getUserIdentityDetails(mainCandidateId);
            if(!mainDetailEnv.success || !mainDetailEnv.data)
            {
                return false;
            }
            return canAcceptTransferAsMain(accountIdentities, userIdentityId, mainDetailEnv.data as UserIdentityDetailDto);
        };

        resolveCanAcceptTransferAsMain().then((canPromote) =>
        {
            if(!cancelled)
            {
                setCanAcceptTransferAsMainOption(canPromote);
            }
        })
        .catch(() =>
        {
            if(!cancelled)
            {
                setCanAcceptTransferAsMainOption(false);
            }
        })
        .finally(() =>
        {
            if(!cancelled)
            {
                setAcceptTransferOptionsLoading(false);
            }
        });

        return () =>
        {
            cancelled = true;
        };
    }, [showAcceptTransferPanel, userIdentityId]);

    // Enlace antiguo del aviso (?acceptTransfer=1): sobre un interviniente que no está en TRANSFER no significa nada
    // y se quita de la URL, conservando el resto de la query y el estado (id y contexto de contrato). En TRANSFER no
    // hace falta: el formulario de aceptación sale igual.
    useEffect(() =>
    {
        if(mode !== "edit" || isLoading || loadedIdentityDetail === null || Number(loadedIdentityDetail.id) !== Number(userIdentityId))
        {
            return;
        }
        if(!shouldRemoveAcceptTransferParam(location.search, loadedIdentityDetail.status))
        {
            return;
        }
        history.replace(
        {
            pathname: location.pathname,
            search: removeAcceptTransferParam(location.search),
            hash: location.hash,
            state: location.state,
        });
    }, [mode, isLoading, loadedIdentityDetail, userIdentityId, location, history]);

    // Validación para menores
    useEffect(() =>
    {
        if(mode === "createMinor" || formData.isMinor)
        {
            setValidation((prev) =>
            ({
                ...prev,
                email: true,
                phone: true,
                operationPhone: true,
                economicActivity: true,
                proofOfIncome: true
            }));
        }
    }, [mode, formData.isMinor]);

    // Presencia del justificante de tutela. Se mantiene siempre, no solo en los flujos de menor: quien
    // decide si hace falta es la fecha de nacimiento, así que la validación tiene que estar poblada también
    // en un alta por el flujo de adulto. La exigencia se aplica aparte (requiresMinorGuardianDocument).
    useEffect(() =>
    {
        const hasProofOfMinorGuardian = hasStoredProofOfMinorGuardian || !!formData.proofOfMinorGuardian;
        setValidation((prev) => prev.proofOfMinorGuardian === hasProofOfMinorGuardian ? prev : {...prev, proofOfMinorGuardian: hasProofOfMinorGuardian});
    }, [hasStoredProofOfMinorGuardian, formData.proofOfMinorGuardian]);

    const prevIsMinorRef = useRef(formData.isMinor);
    useEffect(() =>
    {
        const wasMinor = prevIsMinorRef.current;
        prevIsMinorRef.current = formData.isMinor;
        if(mode === "edit" || wasMinor || !formData.isMinor)
        {
            return;
        }
        setFormData((prev) =>
        ({
            ...prev,
            email: "",
            countryPrefix: "",
            phone: "",
            operationCountryPrefix: "",
            operationPhone: "",
            signatureCountryPrefix: "",
            signaturePhone: "",
            economicActivity: null,
            proofOfIncome: null,
            gafiQuestion: undefined,
            publicPosition: undefined,
            maxContribution: null,
            maritalStatus: null,
            profession: null,
            residenceCountry: null,
            fatcaStatus: null,
            residenceOfficialDocument: null,
            noResidenceDocument: null,
            hasTaxIdInUS: undefined,
            hasResidenceInUS: undefined,
            hasTaxObligationInUS: undefined,
        }));
    }, [formData.isMinor, mode]);

    // Sincronización de teléfonos cuando useSinglePhone está activo
    useEffect(() =>
    {
        if(formData.useSinglePhone)
        {
            setFormData((prev) =>
            ({
                ...prev,
                operationCountryPrefix: prev.countryPrefix,
                operationPhone: prev.phone,
                signatureCountryPrefix: prev.countryPrefix,
                signaturePhone: prev.phone
            }));
            setValidation((prev) =>
            ({
                ...prev,
                operationPhone: !!formData.phone,
                signaturePhone: !!formData.phone,
                operationCountryPrefix: !!formData.countryPrefix,
                signatureCountryPrefix: !!formData.countryPrefix
            }));
        }
    }, [formData.useSinglePhone, formData.countryPrefix, formData.phone]);

    // Sincronización dirección postal → fiscal cuando sameAddress está activo
    useEffect(() =>
    {
        if(formData.sameAddress && formData.postalAddress)
        {
            setFormData((prev) =>
            ({
                ...prev,
                fiscalAddress:
                {
                    roadType: prev.postalAddress?.roadType || "",
                    streetName: prev.postalAddress?.streetName || "",
                    streetNumber: prev.postalAddress?.streetNumber || "",
                    streetFloor: prev.postalAddress?.streetFloor || "",
                    streetStair: prev.postalAddress?.streetStair || "",
                    streetDoor: prev.postalAddress?.streetDoor || "",
                    streetOther: prev.postalAddress?.streetOther || "",
                    zipCode: prev.postalAddress?.zipCode || "",
                    city: prev.postalAddress?.city || "",
                    state: prev.postalAddress?.state || "",
                    country: prev.postalAddress?.country || "ES"
                }
            }));
            setValidation((prev) =>
            ({
                ...prev,
                fiscalAddress: isAddressValid(formData.postalAddress)
            }));
        }
    }, [formData.sameAddress, formData.postalAddress]);

    const handleDocumentProcessed = useCallback((result: DocumentProcessingResult, frontPreview: string, backPreview: string) =>
    {
        if(result.success)
        {
            setDocumentPhaseComplete(true);
        }

        const birthdayDate = parseIsoDate(result.dateOfBirth);
        const idExpirationDate = parseIsoDate(result.dateOfExpiration);
        // El análisis puede no devolver fechas (p. ej. en edición, cuando solo se recuperan los
        // adjuntos). En ese caso conservamos las que ya tenía el formulario en lugar de invalidarlas.
        const effectiveBirthday = birthdayDate ?? formDataRef.current.birthday;
        const effectiveIdExpiration = idExpirationDate ?? formDataRef.current.idExpiration;
        // Mismo criterio para los datos personales: la validez se calcula sobre el valor que queda
        // en el formulario, no sobre el resultado del análisis, para no invalidar lo ya cargado.
        const effectiveFirstName = result.firstName || formDataRef.current.firstName;
        const effectiveLastName = result.lastName || formDataRef.current.lastName;
        const effectiveSecondLastName = result.secondLastName || formDataRef.current.secondLastName;
        const effectiveIdDocument = result.documentNumber || formDataRef.current.idDocument;
        // El análisis ha leído "PERMANENTE" en la validez: se premarca el check y se descarta la fecha.
        // Nunca se desmarca desde el análisis, para no perder lo que el usuario ya hubiera indicado.
        const effectiveHasPermanentIdExpiration = result.hasPermanentIdExpiration === true || formDataRef.current.hasPermanentIdExpiration;
        const dateBusinessValidation = getDateBusinessValidation(effectiveBirthday, effectiveIdExpiration, mode, effectiveHasPermanentIdExpiration);
        const {idFront: effectiveIdFront, idBack: effectiveIdBack} = resolveDocumentAttachments(result, frontPreview, backPreview, formDataRef.current);

        setFormData((prev) =>
        ({
            ...prev,
            firstName: result.firstName || prev.firstName,
            lastName: result.lastName || prev.lastName,
            secondLastName: result.secondLastName || prev.secondLastName,
            idDocument: result.documentNumber || prev.idDocument,
            idType: result.documentType === "DNI" || result.documentType === "NIE" ? result.documentType : prev.idType,
            birthday: birthdayDate ?? prev.birthday,
            // Aunque el análisis marque validez permanente conservamos la fecha previa (no se envía
            // mientras el check esté activo), coherente con no perderla ante un clic accidental.
            idExpiration: effectiveHasPermanentIdExpiration ? prev.idExpiration : (idExpirationDate ?? prev.idExpiration),
            hasPermanentIdExpiration: effectiveHasPermanentIdExpiration,
            idFront: effectiveIdFront,
            idBack: effectiveIdBack,
        }));
        setValidation((prev) =>
        ({
            ...prev,
            // Lo que rellena el análisis del DNI pasa por las mismas reglas que lo escrito a mano; lo que ya
            // estaba en el formulario conserva su validez.
            firstName: result.firstName ? isValidPersonName(result.firstName) : !!effectiveFirstName,
            lastName: result.lastName ? isValidPersonName(result.lastName) : !!effectiveLastName,
            secondLastName: result.secondLastName ? isValidPersonName(result.secondLastName) : !!effectiveSecondLastName,
            idDocument: !!effectiveIdDocument,
            birthday: !!effectiveBirthday && !dateBusinessValidation.hasInvalidBirthday,
            idExpiration: (effectiveHasPermanentIdExpiration || !!effectiveIdExpiration) && !dateBusinessValidation.hasInvalidIdExpiration,
            idFront: !!effectiveIdFront,
            idBack: !!effectiveIdBack,
        }));
        setValidationErrors((prev) =>
        {
            let base = prev.filter((e) => e.field !== BIRTHDAY_ERROR_FIELD && e.field !== ID_EXPIRATION_ERROR_FIELD);
            if(dateBusinessValidation.hasInvalidBirthday)
            {
                base = upsertValidationError(base, BIRTHDAY_ERROR_FIELD, dateBusinessValidation.birthdayErrorMessage);
            }
            if(dateBusinessValidation.hasInvalidIdExpiration)
            {
                base = upsertValidationError(base, ID_EXPIRATION_ERROR_FIELD, dateBusinessValidation.idExpirationErrorMessage);
            }
            return base;
        });
        setFormState("form_filling");
    }, [mode]);

    // Abre la edición de otro interviniente propio en la misma ruta, con el id por estado (anti-IDOR) y conservando
    // el contexto de contrato: el del listado (state) y el del deep-link MTA (querystring).
    const navigateToOwnIdentityEdit = useCallback((targetIdentityId: number): void =>
    {
        const navigationState: IdentityNavigationState =
        {
            userIdentityId: targetIdentityId,
            contractSavingsPlanId: contractSavingsPlanId,
        };
        history.push(
        {
            pathname: IdentityEditPath,
            search: removeAcceptTransferParam(location.search),
            state: navigationState,
        });
    }, [history, location.search, contractSavingsPlanId]);

    // Solo se llama cuando la fase es flow_resolved (el ciclo terminó con éxito).
    // Los errores no llegan aquí: la fase del hook vuelve a idle/dni_required.
    const handleFlowResolved = useCallback(async (action: UserIdentityFlowAction | null, status: UserIdentityStatusDto | null) =>
    {
        // DNI de un interviniente propio en TRANSFER: no hay panel aquí, se abre su edición, que es donde está el
        // formulario de aceptación. El borrador que se estaba editando no se toca.
        if(action === UserIdentityFlowAction.AcceptTransferOwnership)
        {
            const acceptTransferTargetId = resolveAcceptTransferTargetId(action, status?.existingDraftId);
            if(acceptTransferTargetId !== null)
            {
                navigateToOwnIdentityEdit(acceptTransferTargetId);
                return;
            }
            setValidationErrors([{field: "General", message: "No se pudo abrir el interviniente recibido por cesión. Inténtalo de nuevo."}]);
            return;
        }

        saveDerivedIdentityStatusRef.current = false;
        setIdentityStatus(status);
        setFlowRequestError(null);
        setFlowRequestResultMessage(null);
        setDocumentPhaseComplete(true);

        switch(action)
        {
            case UserIdentityFlowAction.StartNewOnboarding:
            case null:
                // Flujo normal: el formulario se desbloquea
                break;

            case UserIdentityFlowAction.ResumeDraft:
                // Cargar el borrador existente automáticamente
                if(status?.existingDraftId)
                {
                    setIsLoading(true);
                    setValidationErrors([]);
                    try
                    {
                        const draftRes = await userIdentitiesService.getUserIdentityDetails(status.existingDraftId);
                        if(draftRes.success && draftRes.data)
                        {
                            applyLoadedIdentityData(draftRes.data as UserIdentityDetailDto);
                        }
                        else
                        {
                            setValidationErrors([{field: "General", message: orchestratorErrorsToSingleMessage(draftRes.errors) || resolveErrorMessage("DB_ERROR", "No se pudo cargar el borrador existente. Inténtalo de nuevo.")}]);
                        }
                    }
                    catch
                    {
                        setValidationErrors([{field: "General", message: resolveErrorMessage("NETWORK_ERROR", "No se pudo cargar el borrador existente. Inténtalo de nuevo.")}]);
                    }
                    finally
                    {
                        setIsLoading(false);
                    }
                }
                break;

            case UserIdentityFlowAction.RequestAccess:
            case UserIdentityFlowAction.InformationManualSignUpRequired:
            case UserIdentityFlowAction.RequestOwnershipTransfer:
            case UserIdentityFlowAction.AutomaticOwnershipTransfer:
            case UserIdentityFlowAction.ReactivateOwn:
                // El formulario se oculta; isFormVisible lo calcula con identityStatus.
                break;

            default:
                break;
        }
    }, [applyLoadedIdentityData, navigateToOwnIdentityEdit]);

    // El panel que salió de un guardado rechazado se refiere al DNI con el que se guardó: si el usuario lo
    // cambia (p. ej. lo había tecleado mal), deja de aplicar.
    const clearSaveDerivedIdentityStatus = useCallback((): void =>
    {
        if(!saveDerivedIdentityStatusRef.current)
        {
            return;
        }
        saveDerivedIdentityStatusRef.current = false;
        setIdentityStatus(null);
        setFlowRequestError(null);
        setFlowRequestResultMessage(null);
    }, []);

    // Guardado rechazado por un DNI que ya existe y con salida posible (solicitar acceso, cesión o recuperar
    // uno propio): en lugar del error genérico se pinta el panel de esa acción y se lleva al usuario hasta él.
    const applySaveConflictFlow = (result: SaveConflictResult, typedDni: string | null | undefined): boolean =>
    {
        const conflictStatus = buildIdentityStatusFromSaveResult(result, typedDni);
        if(conflictStatus === null)
        {
            return false;
        }
        // DNI de un interviniente propio en TRANSFER: sin panel, se abre su edición con el formulario de aceptación.
        const acceptTransferTargetId = resolveAcceptTransferTargetId(conflictStatus.action, conflictStatus.existingDraftId);
        if(acceptTransferTargetId !== null)
        {
            navigateToOwnIdentityEdit(acceptTransferTargetId);
            return true;
        }
        saveDerivedIdentityStatusRef.current = true;
        setIdentityStatus(conflictStatus);
        setFlowRequestError(null);
        setFlowRequestResultMessage(null);
        setValidationErrors([]);
        setTimeout(() => flowPanelRef.current?.focus(), 0);
        return true;
    };

    const handleDocumentError = useCallback((error: string) =>
    {
        if(!error.trim())
        {
            setValidationErrors((prev) => prev.filter((e) => e.field !== "Documento"));
            return;
        }
        setValidationErrors((prev) =>
        {
            const withoutDoc = prev.filter((e) => e.field !== "Documento");
            return [...withoutDoc, {field: "Documento", message: error}];
        });
        setTimeout(() => errorsRef.current?.focus(), 0);
    }, [errorsRef]);

    const handleDocumentAttachmentErrors = useCallback((errors: ValidationError[]) =>
    {
        setValidationErrors((prev) =>
        {
            const withoutDocumentAttachments = prev.filter((e) => !DOCUMENT_ATTACHMENT_ERROR_FIELDS.includes(e.field));
            return [...withoutDocumentAttachments, ...errors];
        });
        if(errors.length > 0)
        {
            setTimeout(() => errorsRef.current?.focus(), 0);
        }
    }, [errorsRef]);

    const handleFinanceAttachmentErrors = useCallback((errors: ValidationError[]) =>
    {
        setValidationErrors((prev) =>
        {
            const withoutFinanceAttachments = prev.filter((e) => !FINANCE_ATTACHMENT_ERROR_FIELDS.includes(e.field));
            return [...withoutFinanceAttachments, ...errors];
        });
        if(errors.length > 0)
        {
            setTimeout(() => errorsRef.current?.focus(), 0);
        }
    }, [errorsRef]);

    const handleServerWarning = useCallback((message: string) =>
    {
        setServerWarning(message || null);
    }, []);

    const handleAddressCascadeLoadingChange = useCallback((loading: boolean) =>
    {
        setIsAddressCascadeLoading(loading);
    }, []);

    const handleDocumentPreviewLoadingChange = useCallback((loading: boolean) =>
    {
        setIsDocumentPreviewLoading(loading);
    }, []);

    // El DNI fue introducido manualmente: actualizamos formData para que el formulario lo refleje.
    const handleDniConfirmed = useCallback((dni: string) =>
    {
        clearSaveDerivedIdentityStatus();
        setFormData((prev) => ({...prev, idDocument: dni}));
        setValidation((prev) => ({...prev, idDocument: true}));
    }, [clearSaveDerivedIdentityStatus]);

    const handleBirthdayChange = useCallback((date: Date | null) =>
    {
        setFormData((prev) => ({...prev, birthday: date}));

        // Evita mostrar el error mientras el usuario termina de escribir la fecha.
        if(birthdayValidationTimeoutRef.current !== null)
        {
            window.clearTimeout(birthdayValidationTimeoutRef.current);
        }

        setValidation((prev) => ({...prev, birthday: !!date}));
        setValidationErrors((prev) => prev.filter((error) => error.field !== BIRTHDAY_ERROR_FIELD));

        birthdayValidationTimeoutRef.current = window.setTimeout(() =>
        {
            // Se reutiliza la regla compartida (solo interesa la parte de nacimiento; la validez del
            // documento la valida su propio handler, de ahí el null).
            const {hasInvalidBirthday, birthdayErrorMessage} = getDateBusinessValidation(date, null, mode);

            setValidation((prev) => ({...prev, birthday: !!date && !hasInvalidBirthday}));
            setValidationErrors((prev) =>
            {
                const withoutBirthdayError = prev.filter((error) => error.field !== BIRTHDAY_ERROR_FIELD);
                if(!hasInvalidBirthday)
                {
                    return withoutBirthdayError;
                }
                return upsertValidationError(withoutBirthdayError, BIRTHDAY_ERROR_FIELD, birthdayErrorMessage);
            });

            if(hasInvalidBirthday)
            {
                setTimeout(() => errorsRef.current?.focus(), 0);
            }
        }, BIRTHDAY_VALIDATION_DEBOUNCE_MS);
    }, [mode]);

    const handleIdExpirationChange = useCallback((date: Date | null) =>
    {
        setFormData((prev) => ({...prev, idExpiration: date}));

        // Evita mostrar el error mientras el usuario termina de escribir la fecha.
        if(idExpirationValidationTimeoutRef.current !== null)
        {
            window.clearTimeout(idExpirationValidationTimeoutRef.current);
        }

        setValidation((prev) => ({...prev, idExpiration: !!date}));
        setValidationErrors((prev) => prev.filter((error) => error.field !== ID_EXPIRATION_ERROR_FIELD));

        idExpirationValidationTimeoutRef.current = window.setTimeout(() =>
        {
            const todayStart = toStartOfDay(new Date());
            const selectedDateStart = date ? toStartOfDay(date) : null;
            const hasInvalidIdExpirationForCreate = mode !== "edit" && selectedDateStart !== null && selectedDateStart.getTime() <= todayStart.getTime();
            const hasInvalidIdExpirationForEdit = mode === "edit" && selectedDateStart !== null && selectedDateStart.getTime() < todayStart.getTime();
            const hasInvalidIdExpiration = hasInvalidIdExpirationForCreate || hasInvalidIdExpirationForEdit;
            const idExpirationErrorMessage = mode === "edit" ? ID_EXPIRATION_EDIT_ERROR_MESSAGE : ID_EXPIRATION_CREATE_ERROR_MESSAGE;

            setValidation((prev) => ({...prev, idExpiration: !!date && !hasInvalidIdExpiration}));
            setValidationErrors((prev) =>
            {
                const withoutIdExpirationError = prev.filter((error) => error.field !== ID_EXPIRATION_ERROR_FIELD);
                if(!hasInvalidIdExpiration)
                {
                    return withoutIdExpirationError;
                }
                return upsertValidationError(withoutIdExpirationError, ID_EXPIRATION_ERROR_FIELD, idExpirationErrorMessage);
            });

            if(hasInvalidIdExpiration)
            {
                setTimeout(() => errorsRef.current?.focus(), 0);
            }
        }, ID_EXPIRATION_VALIDATION_DEBOUNCE_MS);
    }, [mode]);

    // Validez permanente: al marcarla el campo de fecha se inutiliza pero NO se limpia, para no perder
    // la fecha si el clic fue accidental. La fecha conservada no viaja al backend mientras el check
    // esté activo (el mapper envía null y el backend resuelve el centinela). Al desmarcarla la fecha
    // vuelve a ser obligatoria y se revalida el valor que quede en el campo.
    const handlePermanentIdExpirationToggle = useCallback((ev: React.MouseEvent<HTMLElement>, checked?: boolean) =>
    {
        const isChecked = checked || false;

        // Un cambio de fecha reciente puede tener validación en vuelo: si dispara después de marcar
        // el check volvería a pintar el error de caducidad sobre un campo ya deshabilitado.
        if(idExpirationValidationTimeoutRef.current !== null)
        {
            window.clearTimeout(idExpirationValidationTimeoutRef.current);
            idExpirationValidationTimeoutRef.current = null;
        }

        setFormData((prev) => ({...prev, hasPermanentIdExpiration: isChecked}));

        if(isChecked)
        {
            // Marcado: la validez la aporta el check, el campo de fecha queda válido pase lo que pase.
            setValidation((prev) => ({...prev, idExpiration: true}));
            setValidationErrors((prev) => prev.filter((error) => error.field !== ID_EXPIRATION_ERROR_FIELD));
            return;
        }

        // Desmarcado: vuelve a mandar la fecha. Se revalida contra las reglas de caducidad la que haya
        // quedado conservada (lectura síncrona vía formDataRef); si no hay fecha, el campo queda pendiente.
        const current = formDataRef.current;
        const dateBusinessValidation = getDateBusinessValidation(current.birthday, current.idExpiration, mode, false);
        setValidation((prev) => ({...prev, idExpiration: !!current.idExpiration && !dateBusinessValidation.hasInvalidIdExpiration}));
        setValidationErrors((prev) =>
        {
            const withoutIdExpirationError = prev.filter((error) => error.field !== ID_EXPIRATION_ERROR_FIELD);
            if(!dateBusinessValidation.hasInvalidIdExpiration)
            {
                return withoutIdExpirationError;
            }
            return upsertValidationError(withoutIdExpirationError, ID_EXPIRATION_ERROR_FIELD, dateBusinessValidation.idExpirationErrorMessage);
        });
    }, [mode]);

    useEffect(() =>
    {
        return () =>
        {
            if(birthdayValidationTimeoutRef.current !== null)
            {
                window.clearTimeout(birthdayValidationTimeoutRef.current);
            }
            if(idExpirationValidationTimeoutRef.current !== null)
            {
                window.clearTimeout(idExpirationValidationTimeoutRef.current);
            }
        };
    }, []);

    const handleProofOfMinorGuardianChange = useCallback((file: File | null) =>
    {
        setFormData((prev) => ({...prev, proofOfMinorGuardian: file}));
        setValidation((prev) => ({...prev, proofOfMinorGuardian: !!file}));
    }, []);

    const handleProofOfIncomeChange = useCallback((file: File | null) =>
    {
        setFormData((prev) => ({...prev, proofOfIncome: file}));
        setValidation((prev) => ({...prev, proofOfIncome: !!file}));
    }, []);

    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    {
        const {name, value} = e.target;
        if(name === "idDocument")
        {
            clearSaveDerivedIdentityStatus();
        }
        setFormData((prev) =>
        {
            let newData: IdentityFormData = {...prev};

            if(name === "gafiQuestion" || name === "publicPosition" || name === "hasTaxIdInUS" || name === "hasResidenceInUS" || name === "hasTaxObligationInUS")
            {
                newData = {...prev, [name]: value === "true"};
            }
            else if((KycBooleanFields as readonly string[]).includes(name))
            {
                const answer = value === "true";
                newData = {...prev, [name]: answer};
                // Coherencia puerta → dato (espejo del auto-saneado del backend).
                if(name === "otherNationalities" && !answer)
                {
                    newData.otherNationality = "";
                }
                if(name === "hasEmployedActivity")
                {
                    newData.employedActivities = answer ?
                        (prev.employedActivities.length > 0 ? prev.employedActivities : [{activityStatus: null, cnoCode: null, years: null, employedCurrentCompany: null}]) :
                        [];
                }
                if(name === "hasSelfEmployedActivity")
                {
                    newData.selfEmployedActivities = answer ?
                        (prev.selfEmployedActivities.length > 0 ? prev.selfEmployedActivities : [{activityStatus: null, cnaeCode: null, years: null, relatedProfessionalActivity: null}]) :
                        [];
                }
            }
            else if((KycStringCodeFields as readonly string[]).includes(name))
            {
                newData = {...prev, [name]: value === "" ? null : value};
                // La documentación acreditativa depende del origen: al cambiarlo se limpia la selección.
                if(name === "fundsOrigin")
                {
                    newData.fundsDocumentationProvided = null;
                }
            }
            else if(name === "numberOfDependants")
            {
                // Coherencia lógica: solo enteros no negativos (los negativos se llevan a 0).
                const parsedDependants = Number(value);
                newData = {...prev, numberOfDependants: value === "" || Number.isNaN(parsedDependants) ? null : Math.max(0, Math.trunc(parsedDependants))};
            }
            else if(name === "maxContribution")
            {
                const parsedMaxContribution = Number(value);
                newData = {...prev, maxContribution: value === "" || Number.isNaN(parsedMaxContribution) ? null : parsedMaxContribution};
            }
            else if(name === "residenceCountry")
            {
                newData =
                {
                    ...prev,
                    residenceCountry: value,
                    ...(value === "ES" ? {noResidenceDocument: null} : {residenceOfficialDocument: null})
                };
            }
            else if(name === "maritalStatus" || name === "fatcaStatus" || name === "residenceOfficialDocument" || name === "noResidenceDocument")
            {
                newData = {...prev, [name]: value === "" ? null : Number(value)};
                // El régimen matrimonial (KYC) solo aplica a Casado: al cambiar el estado civil se limpia.
                if(name === "maritalStatus" && Number(value) !== MaritalStatusMarriedValue)
                {
                    newData.maritalSituation = null;
                }
            }
            else
            {
                newData = {...prev, [name]: value};
            }

            if(prev.useSinglePhone && (name === "phone" || name === "countryPrefix"))
            {
                newData.operationCountryPrefix = name === "countryPrefix" ? String(value) : prev.countryPrefix;
                newData.operationPhone = name === "phone" ? String(value) : prev.phone;
                newData.signatureCountryPrefix = name === "countryPrefix" ? String(value) : prev.countryPrefix;
                newData.signaturePhone = name === "phone" ? String(value) : prev.phone;
            }

            return newData;
        });

        if(name in VALIDATION_FIELDS)
        {
            const booleanFields = ["gafiQuestion", "publicPosition", "hasTaxIdInUS", "hasResidenceInUS", "hasTaxObligationInUS"];
            const numericCatalogFields = ["maritalStatus", "fatcaStatus", "residenceOfficialDocument", "noResidenceDocument"];
            let isValid: boolean;
            if(booleanFields.includes(name))
            {
                isValid = true;
            }
            else
            if(numericCatalogFields.includes(name))
            {
                if(name === "fatcaStatus")
                {
                    const selectedValue = value === "" ? null : Number(value);
                    const selectedLabel = getCatalogLabelByValue(identityCatalogsRef.current?.FatcaStatuses, selectedValue);
                    isValid = selectedValue !== null && !Number.isNaN(selectedValue) && isAllowedFatcaStatus(selectedLabel);
                }
                else
                {
                    isValid = value !== "" && !Number.isNaN(Number(value));
                }
            }
            else if(PersonNameFields.includes(name))
            {
                // Mismas reglas de formato que el backend (PersonNameRules).
                isValid = isValidPersonName(value);
            }
            else
            {
                isValid = !!value;
            }
            setValidation((prev) =>
            {
                const next = {...prev, [name]: isValid} as FormValidation;
                if(name === "residenceCountry")
                {
                    // Solo se invalida el documento del caso que se limpia en formData (espejo de la
                    // limpieza de arriba). El documento del caso vigente se conserva, así que su
                    // validación no se toca: si ya tenía un valor válido (p. ej. "Ninguno", código 0)
                    // no debe volver a marcarse como pendiente.
                    if(value === "ES")
                    {
                        next.noResidenceDocument = false;
                    }
                    else
                    {
                        next.residenceOfficialDocument = false;
                    }
                }
                return next;
            });
        }
    }, [clearSaveDerivedIdentityStatus]);

    // ─── Filas de actividades económicas KYC (la validación la recalcula el efecto del bloque) ───

    const handleKycActivityAdd = useCallback((kind: KycActivityKind): void =>
    {
        setFormData((prev) =>
        {
            if(kind === "employed")
            {
                const newRow: KycEmployedActivityRow = {activityStatus: null, cnoCode: null, years: null, employedCurrentCompany: null};
                return {...prev, employedActivities: [...prev.employedActivities, newRow]};
            }
            const newSelfRow: KycSelfEmployedActivityRow = {activityStatus: null, cnaeCode: null, years: null, relatedProfessionalActivity: null};
            return {...prev, selfEmployedActivities: [...prev.selfEmployedActivities, newSelfRow]};
        });
    }, []);

    const handleKycActivityRemove = useCallback((kind: KycActivityKind, index: number): void =>
    {
        setFormData((prev) =>
        {
            if(kind === "employed")
            {
                return {...prev, employedActivities: prev.employedActivities.filter((_, i) => i !== index)};
            }
            return {...prev, selfEmployedActivities: prev.selfEmployedActivities.filter((_, i) => i !== index)};
        });
    }, []);

    const handleKycActivityChange = useCallback((kind: KycActivityKind, index: number, field: string, value: string | number | null): void =>
    {
        setFormData((prev) =>
        {
            if(kind === "employed")
            {
                const rows = prev.employedActivities.map((row, i) => (i === index ? {...row, [field]: value} : row));
                return {...prev, employedActivities: rows};
            }
            const selfRows = prev.selfEmployedActivities.map((row, i) => (i === index ? {...row, [field]: value} : row));
            return {...prev, selfEmployedActivities: selfRows};
        });
    }, []);

    const handlePostalAddressChange = useCallback((field: string, value: string) =>
    {
        setFormData((prev) =>
        {
            const newPostalAddress = prev.postalAddress ?
            {
                ...prev.postalAddress,
                [field]: value
            } :
            {
                roadType: "",
                streetName: "",
                streetNumber: "",
                streetFloor: "",
                streetStair: "",
                streetDoor: "",
                streetOther: "",
                zipCode: "",
                city: "",
                state: "",
                country: "ES",
                ...{[field]: value}
            };

            const newFiscalAddress = prev.sameAddress ?
            {
                roadType: newPostalAddress.roadType || "",
                streetName: newPostalAddress.streetName || "",
                streetNumber: newPostalAddress.streetNumber || "",
                streetFloor: newPostalAddress.streetFloor || "",
                streetStair: newPostalAddress.streetStair || "",
                streetDoor: newPostalAddress.streetDoor || "",
                streetOther: newPostalAddress.streetOther || "",
                zipCode: newPostalAddress.zipCode || "",
                city: newPostalAddress.city || "",
                state: newPostalAddress.state || "",
                country: newPostalAddress.country || "ES"
            } : prev.fiscalAddress;

            return {...prev, postalAddress: newPostalAddress, fiscalAddress: newFiscalAddress};
        });
    }, []);

    const handleFiscalAddressChange = useCallback((field: string, value: string) =>
    {
        setFormData((prev) =>
        ({
            ...prev,
            fiscalAddress: prev.fiscalAddress ?
            {
                ...prev.fiscalAddress,
                [field]: value
            } :
            {
                roadType: "",
                streetName: "",
                streetNumber: "",
                streetFloor: "",
                streetStair: "",
                streetDoor: "",
                streetOther: "",
                zipCode: "",
                city: "",
                state: "",
                country: "ES",
                ...{[field]: value}
            }
        }));
    }, []);

    const handleUseSinglePhoneToggle = useCallback((ev: React.MouseEvent<HTMLElement>, checked?: boolean) =>
    {
        const isChecked = checked || false;
        setFormData((prev) =>
        {
            if(isChecked)
            {
                const newData =
                {
                    ...prev,
                    useSinglePhone: true,
                    operationCountryPrefix: prev.countryPrefix,
                    operationPhone: prev.phone,
                    signatureCountryPrefix: prev.countryPrefix,
                    signaturePhone: prev.phone
                };
                setValidation((prevVal) =>
                ({
                    ...prevVal,
                    operationPhone: !!prev.phone,
                    signaturePhone: !!prev.phone,
                    operationCountryPrefix: !!prev.countryPrefix,
                    signatureCountryPrefix: !!prev.countryPrefix
                }));
                return newData;
            }
            else
            {
                setValidation((prevVal) =>
                ({
                    ...prevVal,
                    operationPhone: false,
                    signaturePhone: false,
                    operationCountryPrefix: false,
                    signatureCountryPrefix: false
                }));
                return{
                    ...prev,
                    useSinglePhone: false,
                    operationCountryPrefix: "",
                    operationPhone: "",
                    signatureCountryPrefix: "",
                    signaturePhone: ""
                };
            }
        });
    }, []);

    const handleSameAddressToggle = useCallback((ev: React.MouseEvent<HTMLElement>, checked?: boolean) =>
    {
        setFormData((prev) =>
        {
            const isChecked = checked !== undefined ? checked : !prev.sameAddress;
            if(isChecked)
            {
                const postal = prev.postalAddress;
                const newFiscalAddress =
                {
                    roadType: postal?.roadType || "",
                    streetName: postal?.streetName || "",
                    streetNumber: postal?.streetNumber || "",
                    streetFloor: postal?.streetFloor || "",
                    streetStair: postal?.streetStair || "",
                    streetDoor: postal?.streetDoor || "",
                    streetOther: postal?.streetOther || "",
                    zipCode: postal?.zipCode || "",
                    city: postal?.city || "",
                    state: postal?.state || "",
                    country: postal?.country || "ES"
                };
                setValidation((prevVal) =>
                ({
                    ...prevVal,
                    fiscalAddress: isAddressValid(newFiscalAddress)
                }));
                return {...prev, sameAddress: true, fiscalAddress: newFiscalAddress};
            }
            else
            {
                setValidation((prevVal) => ({...prevVal, fiscalAddress: false}));
                return {...prev, sameAddress: false, fiscalAddress: null};
            }
        });
    }, []);

    const isFormValid = (): boolean =>
    {
        // En edición, si el detalle llega con el documento adjunto pero sin número, la fase de
        // documento queda abierta. Basta con que el usuario introduzca el DNI (en el bloque de
        // documento o en el propio formulario) para cerrarla: no tiene sentido bloquear el
        // guardado por un número que ya tenemos.
        const hasManualIdDocument = mode === "edit" && String(formData.idDocument ?? "").trim() !== "";
        if(!documentPhaseComplete && !hasManualIdDocument)
        {
            return false;
        }

        const contactValidation = formData.isMinor ? true : (validation.email && validation.phone && validation.countryPrefix);

        const basicValidation =
            validation.firstName && validation.lastName && validation.secondLastName &&
            contactValidation &&
            validation.birthday && validation.birthdayCountry && validation.nationalCountry &&
            validation.idDocument && validation.idExpiration &&
            validation.postalAddress;

        const phoneValidation = formData.isMinor || formData.useSinglePhone ?
            true :
            (validation.operationPhone && validation.signaturePhone && validation.operationCountryPrefix && validation.signatureCountryPrefix);

        const fiscalAddressValidation = formData.sameAddress ? true : validation.fiscalAddress;

        const financialValidation = formData.isMinor ?
            validation.proofOfMinorGuardian :
            (validation.economicActivity && validation.proofOfIncome && validation.gafiQuestion && validation.publicPosition);

        // Aditiva: no relaja nada, solo añade lo que el backend exige por fecha de nacimiento. Sin esto el
        // cliente dejaba guardar un alta de adulto con fecha de menor y el servidor la rechazaba con
        // PROOF_OF_MINOR_GUARDIAN_REQUIRED, sin que el formulario mostrase siquiera el bloque de tutela.
        const minorGuardianValidation = !requiresMinorGuardianDocument || validation.proofOfMinorGuardian;

        const residenceDocOk = formData.residenceCountry === "ES" ?
            validation.residenceOfficialDocument :
            (!!formData.residenceCountry && validation.noResidenceDocument);

        const fiscalDataValidation = formData.isMinor ? true :
            (!catalogsError &&
            validation.maritalStatus && validation.residenceCountry && validation.fatcaStatus &&
            residenceDocOk && validation.hasTaxIdInUS && validation.hasResidenceInUS && validation.hasTaxObligationInUS);

        // El bloque KYC aplica a todos los intervinientes (el backend lo exige siempre en ProcessAndSync).
        const kycDataValidation =
            !professionCatalogsError &&
            validation.annualGrossIncome && validation.hasEmployedActivity && validation.hasSelfEmployedActivity &&
            validation.employedActivities && validation.selfEmployedActivities && validation.fundsOrigin &&
            validation.fundsDocumentationProvided && validation.internationalCommercialActivity && validation.isPepRelative &&
            validation.numberOfDependants && validation.otherNationalities && validation.otherNationality &&
            validation.otherPersonalCircumstances && validation.riskCountries && validation.riskJurisdictionActivity &&
            validation.maritalSituation && validation.isUsCitizen;

        if(isFiscalBlocked || hasSaveBlockingKycAnswers(formData) || isExpiredIdentityDocumentRenewalIncomplete)
        {
            return false;
        }

        if(mode === "edit")
        {
            if(isLoading || catalogsLoading || isAddressCascadeLoading || isDocumentPreviewLoading)
            {
                return false;
            }
            return isEditFormValid(formData, validation) && kycDataValidation && minorGuardianValidation;
        }

        return !!(basicValidation && phoneValidation && fiscalAddressValidation && financialValidation && fiscalDataValidation && kycDataValidation && minorGuardianValidation && (formData.idType === "DNI" || formData.idType === "NIE"));
    };

    // El bloqueo KYC no impide guardar (D1: la respuesta se persiste y el servidor registra el
    // intento), pero sí finalizar el alta/edición. Excepción: las respuestas de persona con
    // responsabilidad pública invalidan el formulario completo (ver hasSaveBlockingKycAnswers).
    // Las imágenes del documento solo se exigen para finalizar, igual que en el backend: el guardado
    // de borrador las omite (ValidateAllForDraftSave) y ProcessAndSync sí las reclama.
    const hasIdentityDocuments = validation.idFront && validation.idBack;
    const canFinalizeSignUp = resolveSaveUserIdentityDraftId(formData.id, userIdentityId, mainNewDraftId) > 0 && isFormValid() && hasIdentityDocuments && !isKycBlocked;

    const handleCancel = (): void =>
    {
        // En flujos especiales (formulario oculto) también volvemos al listado,
        // ya que ese número de documento no permite el flujo normal del formulario.
        // Deep-link de contrato: vuelve al mapa.
        navigateAfterForm();
    };

    const getFlowActionTargetIdentityId = useCallback((): number | null =>
    {
        const candidates =
        [
            identityStatus?.existingDraftId,
            formData.id,
            userIdentityId,
        ];
        for(const value of candidates)
        {
            const parsed = Number(value ?? 0);
            if(parsed > 0)
            {
                return parsed;
            }
        }
        return null;
    }, [identityStatus?.existingDraftId, formData.id, userIdentityId]);

    const getPerformedActionMessage = useCallback((flowAction: string | null | undefined): string =>
    {
        switch(flowAction)
        {
            case "RequestAccess":
                return "Se ha registrado correctamente la solicitud de acceso para el interviniente asociado al documento.";
            case "RequestOwnershipTransfer":
                return "Se ha solicitado correctamente la cesión de titularidad para este interviniente.";
            case "AutomaticOwnershipTransfer":
                return "La titularidad se ha transferido correctamente para este interviniente.";
            default:
                return "La operación se ha procesado correctamente para este documento.";
        }
    }, []);

    // Ejecuta la acción del panel. promoteToMain solo lo tiene en cuenta el backend en la cesión automática: con
    // true el interviniente llega como principal y el principal vacío de nacimiento se borra en la misma operación
    // (si no aplica responde 409 y no cede nada; el mensaje sale en el banner del panel).
    const submitFlowRequestAction = async (promoteToMain: boolean): Promise<void> =>
    {
        const targetIdentityId = getFlowActionTargetIdentityId();
        if(!targetIdentityId)
        {
            setFlowRequestError("No se pudo resolver el interviniente para ejecutar la acción.");
            return;
        }

        setFlowRequestLoading(true);
        setFlowRequestError(null);
        setFlowRequestResultMessage(null);
        try
        {
            // En la cesión automática el backend exige el DNI analizado (G1-22) y recibe la decisión de principal
            const result = await userIdentitiesService.requestUserIdentityAccess(targetIdentityId, buildRequestAccessPayload(identityStatus, promoteToMain));
            if(!result.success)
            {
                const msg = orchestratorErrorsToSingleMessage(result.errors) || "No se pudo completar la operación.";
                setFlowRequestError(msg);
                setFlowRequestLoading(false);
                return;
            }

            // Transferido como principal: el borrador vacío que se estaba editando ya no existe.
            if(promoteToMain && identityStatus?.action === UserIdentityFlowAction.AutomaticOwnershipTransfer)
            {
                setFlowRequestLoading(false);
                navigateAfterForm(buildMainPromotionSuccessMessage(identityStatus.existingPersonName));
                return;
            }

            const performedAction = typeof result.flow?.action === "string" ? result.flow.action : null;
            setFlowRequestResultMessage(getPerformedActionMessage(performedAction));
            setFlowRequestLoading(false);
        }
        catch
        {
            setFlowRequestError(resolveErrorMessage("NETWORK_ERROR", "Error de red. Inténtalo de nuevo."));
            setFlowRequestLoading(false);
        }
    };

    // Acción por defecto de los paneles (acceso, cesión con aprobación y cesión automática como secundario).
    const handleSubmitFlowRequestAction = async (): Promise<void> =>
    {
        await submitFlowRequestAction(false);
    };

    // El front lo sabe con sus propios datos: el interviniente que se edita es el principal vacío de nacimiento
    // (Main, NEW y sin DNI guardado), o se llegó aquí por la redirección del alta a ese borrador y su detalle aún
    // no está cargado. No se comprueba que sea el único de la cuenta: el backend lo valida y responde 409 si no.
    const canOfferTransferAsMain = isEmptyMainBirthDraft(loadedIdentityDetail) ||
        (loadedIdentityDetail === null && mainNewDraftIdRef.current !== null);

    const handleRequestTransferAsMain = (): void =>
    {
        setPromoteToMainError(null);
        setPromoteToMainIntent("automaticTransfer");
    };

    // Formulario de aceptación, «Aceptar como interviniente principal»: la confirmación avisa de que el principal
    // vacío se elimina y de que es irreversible.
    const handleRequestAcceptTransferAsMain = (): void =>
    {
        setAcceptTransferError(null);
        setPromoteToMainError(null);
        setPromoteToMainIntent("acceptTransfer");
    };

    // Aceptada como secundario: el interviniente ya está en PENDING (u OUTDATED). Se relee su detalle para que el
    // formulario de datos sustituya al de aceptación y pase a ser editable.
    const reloadAcceptedIdentity = async (acceptedIdentityId: number): Promise<void> =>
    {
        setIsLoading(true);
        setValidationErrors([]);
        setServerWarning(null);
        try
        {
            const detailRes = await userIdentitiesService.getUserIdentityDetails(acceptedIdentityId);
            if(detailRes.success && detailRes.data)
            {
                applyLoadedIdentityData(detailRes.data as UserIdentityDetailDto);
            }
            else
            {
                setValidationErrors([{field: "General", message: orchestratorErrorsToSingleMessage(detailRes.errors) || "No se pudieron recargar los datos del interviniente. Recarga la página."}]);
            }
        }
        catch
        {
            setValidationErrors([{field: "General", message: resolveErrorMessage("NETWORK_ERROR", "No se pudieron recargar los datos del interviniente. Recarga la página.")}]);
        }
        finally
        {
            setIsLoading(false);
        }
    };

    // «Aceptar como interviniente secundario» / «Aceptar la cesión»: promoteToMain false.
    const handleAcceptTransfer = async (): Promise<void> =>
    {
        if(!userIdentityId)
        {
            return;
        }

        setAcceptTransferSubmitting(true);
        setAcceptTransferError(null);
        setAcceptTransferSuccessMessage(null);
        try
        {
            const result = await userIdentitiesService.acceptTransferOwnership(userIdentityId, false);
            if(!result.success)
            {
                setAcceptTransferError(orchestratorErrorsToSingleMessage(result.errors) || "No se pudo completar la operación.");
                setAcceptTransferSubmitting(false);
                return;
            }
        }
        catch
        {
            setAcceptTransferError(resolveErrorMessage("NETWORK_ERROR", "Error de red. Inténtalo de nuevo."));
            setAcceptTransferSubmitting(false);
            return;
        }

        setAcceptTransferSubmitting(false);
        setAcceptTransferSuccessMessage("Has aceptado la cesión del interviniente. Ya puedes operar con él.");
        await reloadAcceptedIdentity(userIdentityId);
    };

    const loadedIdentityFullName = [loadedIdentityDetail?.firstName, loadedIdentityDetail?.lastName, loadedIdentityDetail?.secondLastName]
        .map((part) => String(part ?? "").trim())
        .filter(Boolean)
        .join(" ");
    const promoteToMainFullName = promoteToMainIntent === "acceptTransfer"
        ? loadedIdentityFullName
        : String(identityStatus?.existingPersonName ?? "").trim();

    // "No" solo cierra: no se llama a nada ni se guarda la decisión.
    const handleCancelPromoteToMain = (): void =>
    {
        setPromoteToMainIntent(null);
        setPromoteToMainError(null);
    };

    const handleConfirmPromoteToMain = async (): Promise<void> =>
    {
        // Cesión automática: la decisión viaja en el cuerpo de la propia cesión; el resultado sale en el panel.
        if(promoteToMainIntent === "automaticTransfer")
        {
            setPromoteToMainIntent(null);
            await submitFlowRequestAction(true);
            return;
        }

        if(promoteToMainIntent !== "acceptTransfer" || !userIdentityId)
        {
            return;
        }

        // Cesión aprobada (TRANSFER) aceptada como principal; si falla, el mensaje del backend se queda en el modal.
        const promotedFullName = loadedIdentityFullName;
        setPromoteToMainSubmitting(true);
        setPromoteToMainError(null);
        try
        {
            const result = await userIdentitiesService.acceptTransferOwnership(userIdentityId, true);
            if(!result.success)
            {
                setPromoteToMainError(orchestratorErrorsToSingleMessage(result.errors) || "No se pudo completar la operación.");
                setPromoteToMainSubmitting(false);
                return;
            }
            // El principal vacío de nacimiento ya no existe: se vuelve al listado con el aviso de éxito.
            setPromoteToMainSubmitting(false);
            setPromoteToMainIntent(null);
            navigateAfterForm(buildMainPromotionSuccessMessage(promotedFullName));
        }
        catch
        {
            setPromoteToMainError(resolveErrorMessage("NETWORK_ERROR", "Error de red. Inténtalo de nuevo."));
            setPromoteToMainSubmitting(false);
        }
    };

    // ReactivateOwn: analyzeStatus sobre ese DNI reactiva el interviniente propio dado de baja y devuelve
    // ResumeDraft con su id. Se abre su edición; el borrador que se estaba editando no se toca.
    const handleReactivateOwnIdentity = async (): Promise<void> =>
    {
        const dni = String(identityStatus?.dni ?? "").trim().toUpperCase();
        if(dni === "")
        {
            setFlowRequestError("No se pudo resolver el documento del interviniente.");
            return;
        }

        setFlowRequestLoading(true);
        setFlowRequestError(null);
        setFlowRequestResultMessage(null);
        try
        {
            const result = await userIdentitiesService.analyzeIdentityStatus(dni);
            const data = result.data;
            const status = result.success && !!data && typeof data === "object" && "action" in data ? data as UserIdentityStatusDto : null;
            const action = status !== null ? resolveEffectiveFlowAction(result.flow, status.action) : null;
            const reactivatedIdentityId = Number(status?.existingDraftId ?? 0);
            if(action !== UserIdentityFlowAction.ResumeDraft || !(reactivatedIdentityId > 0))
            {
                setFlowRequestError(orchestratorErrorsToSingleMessage(result.errors) || "No se pudo recuperar el interviniente. Inténtalo de nuevo.");
                setFlowRequestLoading(false);
                return;
            }
            setFlowRequestLoading(false);
            navigateToOwnIdentityEdit(reactivatedIdentityId);
        }
        catch
        {
            setFlowRequestError(resolveErrorMessage("NETWORK_ERROR", "Error de red. Inténtalo de nuevo."));
            setFlowRequestLoading(false);
        }
    };

    const handleSubmit = async (): Promise<void> =>
    {
        if(!isFormValid())
        {
            return;
        }

        setIsSaving(true);
        setSavingAction("draft");
        setFormState("submitting");
        setValidationErrors([]);
        setServerWarning(null);
        clearSaveDerivedIdentityStatus();

        try
        {
            const draftId = resolveSaveUserIdentityDraftId(formData.id, userIdentityId, mainNewDraftIdRef.current);
            const typedDni = formData.idDocument;
            const multipart = buildSaveUserIdentityDraftFormData(formData);
            const result = await userIdentitiesService.saveUserIdentityDraft(draftId, multipart);

            if(!result.success && applySaveConflictFlow(result, typedDni))
            {
                setFormState("form_filling");
            }
            else if(!result.success)
            {
                const mapped = mapOrchestratorErrorsToValidationErrors(result.errors);
                if(mapped.length > 0)
                {
                    setValidationErrors(mapped);
                }
                else
                {
                    setValidationErrors([
                    {
                        field: "General",
                        message: resolveErrorMessage("DB_ERROR", "Error al guardar los datos. Inténtalo de nuevo."),
                    }]);
                }
                setFormState("form_filling");
            }
            else
            {
                setFormState("form_filling");
                const savedIdentityId = (result.data as SaveUserIdentityDraftResponseDto | null)?.userIdentityId ?? 0;
                if(savedIdentityId > 0)
                {
                    setFormData((prev) => ({...prev, id: savedIdentityId}));
                }
                setShowSaveSuccessModal(true);

                // Guardar sobre un ACTIVE puede cambiarle el estado en servidor (el backend invalida su KYC
                // y lo pasa a OUTDATED/PENDING), y el usuario se queda en este mismo formulario. Sin releer
                // el detalle, todo lo que se deriva del estado —empezando por el botón de finalizar— seguiría
                // mostrando el interviniente como activo. Solo se refresca el detalle, no el formulario: lo
                // que hay en pantalla es justo lo que se acaba de guardar.
                if(mode === "edit" && savedIdentityId > 0)
                {
                    const refreshed = await userIdentitiesService.getUserIdentityDetails(savedIdentityId);
                    if(refreshed.success && refreshed.data)
                    {
                        const refreshedDetail = refreshed.data as UserIdentityDetailDto;
                        setLoadedIdentityDetail(refreshedDetail);

                        // Qué datos imprime el documento KYC es un detalle interno que el cliente no puede
                        // evaluar, así que no se le pregunta: se le avisa cuando ya ha ocurrido. El backend
                        // saca al interviniente de ACTIVE solo si el cambio invalidó su KYC.
                        const wasActiveBeforeSaving = String(loadedIdentityDetail?.status ?? "").trim().toUpperCase() === "ACTIVE";
                        const isActiveAfterSaving = String(refreshedDetail.status ?? "").trim().toUpperCase() === "ACTIVE";
                        if(wasActiveBeforeSaving && !isActiveAfterSaving)
                        {
                            setServerWarning("Los cambios afectan a los datos de tu documento KYC, así que ha quedado pendiente de firma. Usa 'Finalizar edición' para enviarlos a validación y volver a firmarlo.");
                        }
                    }
                }
            }
        }
        catch
        {
            setValidationErrors([
            {
                field: "General",
                message: resolveErrorMessage("NETWORK_ERROR", "Error al guardar los datos. Inténtalo de nuevo."),
            }]);
            setFormState("form_filling");
        }
        finally
        {
            setIsSaving(false);
            setSavingAction(null);
        }
    };

    const handleFinalizeSignUp = (): void =>
    {
        if(!isFormValid())
        {
            return;
        }

        const runFinalize = async (): Promise<void> =>
        {
            setIsSaving(true);
            setSavingAction("finalize");
            setValidationErrors([]);
            setServerWarning(null);
            clearSaveDerivedIdentityStatus();

            try
            {
                const draftId = resolveSaveUserIdentityDraftId(formData.id, userIdentityId, mainNewDraftIdRef.current);
                const typedDni = formData.idDocument;
                const multipart = buildSaveUserIdentityDraftFormData(formData);
                const saveResult = await userIdentitiesService.saveUserIdentityDraft(draftId, multipart);

                if(!saveResult.success && applySaveConflictFlow(saveResult, typedDni))
                {
                    setFormState("form_filling");
                    return;
                }

                if(!saveResult.success)
                {
                    const mapped = mapOrchestratorErrorsToValidationErrors(saveResult.errors);
                    setValidationErrors(mapped.length > 0 ? mapped : [{field: "General", message: resolveErrorMessage("DB_ERROR", "Error al finalizar el alta/edición. Inténtalo de nuevo.")}]);
                    setFormState("form_filling");
                    return;
                }

                const savedDraft = saveResult.data as SaveUserIdentityDraftResponseDto | null;
                const savedIdentityId = savedDraft?.userIdentityId ?? draftId;
                if(savedIdentityId <= 0)
                {
                    setValidationErrors([{field: "General", message: resolveErrorMessage("DB_ERROR", "No se pudo obtener el identificador del interviniente. Inténtalo de nuevo.")}]);
                    setFormState("form_filling");
                    return;
                }
                setFormData((prev) => ({...prev, id: savedIdentityId}));

                // El guardado ya decide qué sincronización tocaba según el estado; encadenar `process` sin que lo
                // pida repetiría la verificación AML de Pibisi sobre datos idénticos. Ausente (backend anterior)
                // se trata como "sí": un front por delante del backend no puede dejar un alta sin registrar.
                if(savedDraft?.syncPending !== false)
                {
                    const result = await userIdentitiesService.processUserIdentity(savedIdentityId);

                    if(!result.success)
                    {
                        const mapped = mapOrchestratorErrorsToValidationErrors(result.errors);
                        setValidationErrors(mapped.length > 0 ? mapped : [{field: "General", message: resolveErrorMessage("DB_ERROR", "Error al finalizar el alta/edición. Inténtalo de nuevo.")}]);
                        setFormState("form_filling");
                        return;
                    }

                    const rawProcessErrors = result.data?.errors;
                    const processErrors: string[] = Array.isArray(rawProcessErrors) ? rawProcessErrors.filter((e): e is string => typeof e === "string" && e.trim().length > 0) : [];
                    if(processErrors.length > 0)
                    {
                        setValidationErrors(processErrors.map((message) => ({field: "General", message})));
                        setFormState("form_filling");
                        return;
                    }
                }

                const savedSuccessMessage = mode === "edit"
                    ? "Los cambios del interviniente se han guardado correctamente."
                    : "El interviniente se ha dado de alta correctamente.";
                navigateAfterForm(savedSuccessMessage);
            }
            catch
            {
                setValidationErrors([
                {
                    field: "General",
                    message: resolveErrorMessage("NETWORK_ERROR", "Error al finalizar el alta/edición. Inténtalo de nuevo."),
                }]);
                setFormState("form_filling");
            }
            finally
            {
                setIsSaving(false);
                setSavingAction(null);
            }
        };

        void runFinalize();
    };

    const getTitle = (): string =>
    {
        if(showAcceptTransferPanel)
        {
            return "Aceptar cesión de Interviniente";
        }
        switch(mode)
        {
            case "create":
                return "Alta de Interviniente";
            case "createMinor":
                return "Alta de Interviniente Menor de Edad";
            case "edit":
                return "Editar Interviniente";
            default:
                return "Interviniente";
        }
    };

    const getSubtitle = (): string =>
    {
        if(showAcceptTransferPanel)
        {
            return "Acepta la cesión del Interviniente para poder operar con él.";
        }
        if(mode === "edit")
        {
            return "Edita los datos del formulario del Interviniente.";
        }
        switch(formState)
        {
            case "document_upload":
                return "Sube el documento de Identidad del Interviniente.";
            case "form_filling":
                return "Rellena todos los datos del formulario del Interviniente.";
            case "submitting":
                return "Guardando los datos del Interviniente...";
            case "submitted":
                return "Interviniente creado correctamente.";
            default:
                return "";
        }
    };

    // Resalte de los campos que siguen pendientes y bloquean la habilitación del botón.
    // En alta esperamos a que la fase de documento esté completa (antes el formulario está bloqueado).
    // En edición el formulario es editable desde el inicio, así que resaltamos en cuanto los datos
    // del interviniente y los catálogos están cargados: hasta entonces `validation` aún no está
    // hidratada y marcaríamos en amarillo campos que en realidad sí vienen rellenos.
    const highlightPendingManualFields = mode === "edit" ?
        (!isLoading && !catalogsLoading) :
        documentPhaseComplete;

    const closeSaveSuccessModal = useCallback((): void =>
    {
        setShowSaveSuccessModal(false);
    }, []);

    return{
        mode,
        userIdentityId,
        formData,
        validation,
        formState,
        isLoading,
        isSaving,
        savingAction,
        showSaveSuccessModal,
        closeSaveSuccessModal,
        isFormLocked,
        isIdTypeLocked,
        isEmailLocked,
        isEditActiveIdentity,
        canRenewIdentityDocument,
        isMissingIdentityDocuments,
        hasStoredIdentityDocuments,
        isStoredDocumentExpired,
        isStoredIdExpirationMissing,
        requiresMinorGuardianDocument,
        hasBirthdayBusinessError,
        hasIdExpirationBusinessError,
        highlightPendingManualFields,
        validationErrors,
        serverWarning,
        isFiscalBlocked,
        identityCatalogs,
        countriesCatalog,
        catalogsLoading,
        catalogsError,
        professionCatalogsError,
        identityStatus,
        flowRequestLoading,
        flowRequestError,
        flowRequestResultMessage,
        isFormVisible,
        canShowFinalizeAction,
        canFinalizeSignUp,
        isFormValid,
        handleCancel,
        handleSubmit,
        handleFinalizeSignUp,
        handleSubmitFlowRequestAction,
        handleReactivateOwnIdentity,
        flowPanelRef,
        canOfferTransferAsMain,
        handleRequestTransferAsMain,
        showAcceptTransferPanel,
        canAcceptTransferAsMain: canAcceptTransferAsMainOption,
        acceptTransferOptionsLoading,
        acceptTransferSubmitting,
        acceptTransferError,
        acceptTransferSuccessMessage,
        acceptTransferIdentityFullName: loadedIdentityFullName,
        handleRequestAcceptTransferAsMain,
        handleAcceptTransfer,
        promoteToMainFullName,
        showPromoteToMainModal: promoteToMainIntent !== null,
        promoteToMainError,
        promoteToMainSubmitting,
        handleConfirmPromoteToMain,
        handleCancelPromoteToMain,
        handleChange,
        handleKycActivityAdd,
        handleKycActivityRemove,
        handleKycActivityChange,
        setIsKycBlocked,
        handleBirthdayChange,
        handleIdExpirationChange,
        handlePermanentIdExpirationToggle,
        handleProofOfMinorGuardianChange,
        handleProofOfIncomeChange,
        handleUseSinglePhoneToggle,
        handlePostalAddressChange,
        handleFiscalAddressChange,
        handleSameAddressToggle,
        errorsRef,
        handleDocumentProcessed,
        handleDocumentError,
        handleDocumentAttachmentErrors,
        handleFinanceAttachmentErrors,
        handleAddressCascadeLoadingChange,
        handleDocumentPreviewLoadingChange,
        handleServerWarning,
        handleDniConfirmed,
        handleFlowResolved,
        setIsFiscalBlocked,
        getTitle,
        getSubtitle,
    };
}